<template>
    <div class="row justify-content-center">
        <div class="col-lg-11 col-md-10">
            <div class="card bg-secondary shadow border-0">
                <div class="card-header bg-transparent pb-3">
                    <div class="text-muted text-center mt-2 mb-3">
                        <h3>Quran Video Creator</h3>
                        <small>Upload Quran audio, let AI detect ayahs, and create beautiful video overlays</small>
                    </div>
                </div>

                <div class="card-body px-lg-4 py-lg-4">
                    <div class="row mb-4" v-if="currentStep === 1">
                        <div class="col-12">
                            <div class="upload-zone" 
                                 :class="{ 'drag-over': isDragging }"
                                 @dragover.prevent="isDragging = true"
                                 @dragleave.prevent="isDragging = false"
                                 @drop.prevent="handleDrop">
                                <div class="text-center py-5">
                                    <i class="ni ni-cloud-upload-96 display-3 text-primary mb-3"></i>
                                    <h4>Drop your Quran audio file here</h4>
                                    <p class="text-muted">MP3 or WAV, up to 5 minutes</p>
                                    <input type="file" ref="fileInput" @change="handleFileSelect" accept="audio/*" style="display: none">
                                    <base-button type="primary" @click="$refs.fileInput.click()">
                                        Select Audio File
                                    </base-button>
                                </div>
                            </div>
                            <div v-if="analysisError" class="alert alert-danger mt-3 d-flex align-items-start">
                                <i class="fa fa-exclamation-circle mr-2 mt-1"></i>
                                <div>
                                    <strong>Analysis Error</strong>
                                    <p class="mb-0 mt-1">{{ analysisError }}</p>
                                </div>
                                <button type="button" class="close ml-auto" @click="analysisError = null">
                                    <span>&times;</span>
                                </button>
                            </div>
                            <div v-if="audioFile" class="mt-3 p-3 bg-white rounded">
                                <div class="d-flex align-items-center justify-content-between">
                                    <div>
                                        <strong>{{ audioFile.name }}</strong>
                                        <span class="text-muted ml-2">({{ formatFileSize(audioFile.size) }})</span>
                                    </div>
                                    <base-button type="danger" size="sm" @click="removeFile">
                                        <i class="fa fa-times"></i>
                                    </base-button>
                                </div>
                                <audio ref="audioPlayer" :src="audioUrl" controls class="w-100 mt-2"></audio>
                                <div class="mt-3 text-center">
                                    <base-button type="success" @click="analyzeAudio" :disabled="isAnalyzing">
                                        <span v-if="isAnalyzing">
                                            <i class="fa fa-spinner fa-spin"></i> Analyzing...
                                        </span>
                                        <span v-else>
                                            <i class="fa fa-magic"></i> Analyze with AI
                                        </span>
                                    </base-button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="row mb-4" v-if="currentStep === 2">
                        <div class="col-12">
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <h4 class="mb-0">Detected Ayahs</h4>
                                <base-button type="secondary" size="sm" @click="currentStep = 1">
                                    <i class="fa fa-arrow-left"></i> Back
                                </base-button>
                            </div>
                            
                            <div v-if="analysisError" class="alert alert-danger d-flex align-items-start">
                                <i class="fa fa-exclamation-circle mr-2 mt-1"></i>
                                <div>
                                    <strong>Analysis Error</strong>
                                    <p class="mb-0 mt-1">{{ analysisError }}</p>
                                </div>
                                <button type="button" class="close ml-auto" @click="analysisError = null">
                                    <span>&times;</span>
                                </button>
                            </div>

                            <div class="bg-white rounded p-3 mb-3">
                                <div class="row mb-3">
                                    <div class="col-md-6">
                                        <label class="form-label">Detected Surah</label>
                                        <base-dropdown class="w-100">
                                            <base-button slot="title" outline type="primary" class="dropdown-toggle w-100">
                                                {{ detectedSurah > 0 ? surahArray[detectedSurah-1].surah : 'Select Surah' }}
                                            </base-button>
                                            <base-button class="dropdown-item" 
                                                @click="changeSurah(surah.num)" 
                                                v-for="surah in surahArray" 
                                                :key="surah.num">
                                                {{ surah.num + ' - ' + surah.surah }}
                                            </base-button>
                                        </base-dropdown>
                                    </div>
                                    <div class="col-md-6">
                                        <label class="form-label">Font Style</label>
                                        <base-dropdown class="w-100">
                                            <base-button slot="title" outline type="primary" class="dropdown-toggle w-100">
                                                {{ selectedFont.name }}
                                            </base-button>
                                            <base-button class="dropdown-item" 
                                                @click="selectFont(font)" 
                                                v-for="font in fontOptions" 
                                                :key="font.id">
                                                {{ font.name }}
                                            </base-button>
                                        </base-dropdown>
                                    </div>
                                </div>
                            </div>

                            <div class="timeline-container bg-white rounded p-3">
                                <div v-for="(segment, index) in segments" :key="index" class="segment-item mb-3 p-3 border rounded">
                                    <div class="row align-items-center mb-2">
                                        <div class="col-md-3">
                                            <label class="small text-muted mb-1">Start Time (sec)</label>
                                            <input type="number" v-model.number="segment.startTime" 
                                                   class="form-control form-control-sm" 
                                                   step="0.1" min="0" :max="audioDuration"
                                                   @change="onSegmentTimeChange(segment)">
                                        </div>
                                        <div class="col-md-3">
                                            <label class="small text-muted mb-1">End Time (sec)</label>
                                            <input type="number" v-model.number="segment.endTime" 
                                                   class="form-control form-control-sm" 
                                                   step="0.1" min="0" :max="audioDuration"
                                                   @change="onSegmentTimeChange(segment)">
                                        </div>
                                        <div class="col-md-3">
                                            <label class="small text-muted mb-1">Ayah</label>
                                            <select v-model="segment.ayah" class="form-control form-control-sm" @change="fetchAyahText(segment)">
                                                <option :value="0">Basmallah</option>
                                                <option v-for="n in maxAyahs" :key="n" :value="n">Ayah {{ n }}</option>
                                            </select>
                                        </div>
                                        <div class="col-md-3 text-right">
                                            <label class="small text-muted mb-1 d-block">&nbsp;</label>
                                            <base-button type="danger" size="sm" @click="removeSegment(index)">
                                                <i class="fa fa-trash"></i> Remove
                                            </base-button>
                                        </div>
                                    </div>
                                    <div class="arabic-preview p-2 bg-light rounded" dir="rtl" 
                                         :style="getArabicPreviewStyle()">
                                        {{ segment.arabicText || 'Loading...' }}
                                    </div>
                                    
                                    <div v-if="segment.words && segment.words.length > 1 && segment.ayah !== 0" class="word-trim-controls mt-2 p-2 border rounded bg-white">
                                        <div class="d-flex align-items-center justify-content-between mb-2">
                                            <span class="small font-weight-bold">Trim Text ({{ segment.words.length }} words) v2</span>
                                            <div class="form-check form-check-inline mb-0">
                                                <input type="checkbox" class="form-check-input" 
                                                       :id="'manual-' + index" 
                                                       v-model="segment.useManualText"
                                                       @change="updateSegmentDisplayText(segment)">
                                                <label class="form-check-label small" :for="'manual-' + index">Manual Override</label>
                                            </div>
                                        </div>
                                        
                                        <div v-if="!segment.useManualText">
                                            <div class="row align-items-center">
                                                <div class="col-5">
                                                    <label class="small text-muted mb-1">Start Word</label>
                                                    <input type="number" 
                                                           :value="segment.startWord + 1" 
                                                           class="form-control form-control-sm"
                                                           min="1" 
                                                           :max="segment.endWord + 1"
                                                           @input="segment.startWord = Math.max(0, parseInt($event.target.value) - 1); onWordRangeChange(segment)">
                                                </div>
                                                <div class="col-2 text-center">
                                                    <span class="small text-muted">to</span>
                                                </div>
                                                <div class="col-5">
                                                    <label class="small text-muted mb-1">End Word</label>
                                                    <input type="number" 
                                                           :value="segment.endWord + 1" 
                                                           class="form-control form-control-sm"
                                                           :min="segment.startWord + 1" 
                                                           :max="segment.words && segment.words.length ? segment.words.length : 1"
                                                           @input="segment.endWord = Math.max(0, parseInt($event.target.value) - 1); onWordRangeChange(segment)">
                                                </div>
                                            </div>
                                            <div class="word-pills mt-2 p-2 bg-light rounded" dir="rtl" style="max-height: 100px; overflow-y: auto;">
                                                <span v-for="(word, wIdx) in segment.words" 
                                                      :key="wIdx" 
                                                      class="word-pill"
                                                      :class="{ 'selected': wIdx >= segment.startWord && wIdx <= segment.endWord }"
                                                      @click="toggleWordSelection(segment, wIdx)"
                                                      :style="getWordPillStyle()">
                                                    {{ word.text }}
                                                </span>
                                            </div>
                                        </div>
                                        
                                        <div v-else class="mt-2">
                                            <textarea v-model="segment.manualText" 
                                                      class="form-control" 
                                                      dir="rtl"
                                                      rows="2"
                                                      placeholder="Enter the exact Arabic text to display..."
                                                      @input="updateSegmentDisplayText(segment)"></textarea>
                                        </div>
                                    </div>
                                </div>
                                <base-button type="primary" size="sm" @click="addSegment" class="mt-2">
                                    <i class="fa fa-plus"></i> Add Segment
                                </base-button>
                            </div>

                            <div class="mt-3 text-center">
                                <base-button type="success" @click="currentStep = 3" :disabled="segments.length === 0">
                                    Continue to Customization <i class="fa fa-arrow-right"></i>
                                </base-button>
                            </div>
                        </div>
                    </div>

                    <div class="row" v-if="currentStep === 3">
                        <div class="col-md-7">
                            <div class="d-flex justify-content-between align-items-center mb-3">
                                <h4 class="mb-0">Video Preview</h4>
                                <base-button type="secondary" size="sm" @click="currentStep = 2">
                                    <i class="fa fa-arrow-left"></i> Back
                                </base-button>
                            </div>
                            
                            <div class="preview-container" ref="previewContainer">
                                <canvas ref="previewCanvas" :width="videoWidth" :height="videoHeight"></canvas>
                                <div class="overlay-controls" v-if="segments.length > 0">
                                    <div class="text-overlay arabic-overlay" 
                                         :style="getArabicOverlayStyle()"
                                         @mousedown="startDrag('arabic', $event)"
                                         dir="rtl">
                                        <div class="resize-handle resize-e" @mousedown.stop="startResize('arabic', 'e', $event)"></div>
                                        <div class="resize-handle resize-s" @mousedown.stop="startResize('arabic', 's', $event)"></div>
                                        <div class="resize-handle resize-se" @mousedown.stop="startResize('arabic', 'se', $event)"></div>
                                    </div>
                                    <div v-if="showTranslation" 
                                         class="text-overlay translation-overlay"
                                         :style="getTranslationOverlayStyle()"
                                         @mousedown="startDrag('translation', $event)">
                                        <div class="resize-handle resize-e" @mousedown.stop="startResize('translation', 'e', $event)"></div>
                                        <div class="resize-handle resize-s" @mousedown.stop="startResize('translation', 's', $event)"></div>
                                        <div class="resize-handle resize-se" @mousedown.stop="startResize('translation', 'se', $event)"></div>
                                    </div>
                                    <div v-if="clientLogo" 
                                         class="client-logo-overlay"
                                         :style="getClientLogoStyle()"
                                         @mousedown="startDrag('clientLogo', $event)">
                                        <img :src="clientLogo" :style="{ height: clientLogoSize + 'px', width: 'auto', pointerEvents: 'none' }">
                                    </div>
                                    <div v-if="showSurahName && (currentSurahName || surahLogoUrl)" 
                                         class="surah-name-overlay"
                                         :style="getSurahNameStyle()"
                                         @mousedown="startDrag('surahName', $event)">
                                        <img v-if="surahNameType === 'logo' && surahLogoUrl" 
                                             :src="colorizedSurahLogoUrl || surahLogoUrl" 
                                             :style="{ height: surahNameSize + 'px', width: 'auto', pointerEvents: 'none' }"
                                             alt="Surah Name">
                                        <span v-else :style="{ fontSize: surahNameSize + 'px', color: surahNameColor }">{{ currentSurahName }}</span>
                                    </div>
                                    <div v-if="showSurahName && showEnglishSurahName && englishSurahName" 
                                         class="english-surah-overlay"
                                         :style="getEnglishSurahStyle()"
                                         @mousedown="startDrag('englishSurah', $event)">
                                        <span :style="{ fontSize: englishSurahNameSize + 'px', color: englishSurahNameColor }">{{ englishSurahName }}</span>
                                    </div>
                                    <div class="watermark-overlay" :style="getWatermarkStyle()">
                                        <span class="watermark-text">QuranClipHelper.com</span>
                                    </div>
                                </div>
                            </div>

                            <div class="mt-3">
                                <audio ref="previewAudio" :src="audioUrl" preload="auto" playsinline @timeupdate="onTimeUpdate" @ended="onAudioEnded" @pause="onAudioPaused"></audio>
                                <div class="d-flex align-items-center">
                                    <base-button type="primary" @click="togglePlayback">
                                        <i :class="isPlaying ? 'fa fa-pause' : 'fa fa-play'"></i>
                                    </base-button>
                                    <input type="range" 
                                           class="video-seek-slider flex-grow-1 mx-3" 
                                           min="0" 
                                           :max="audioDuration || 100" 
                                           step="0.1"
                                           :value="currentTime"
                                           @mousedown="isSeeking = true"
                                           @mouseup="isSeeking = false"
                                           @input="seekToTime($event.target.value)"
                                           @change="seekToTime($event.target.value)">
                                    <span class="text-muted">{{ formatTime(currentTime) }} / {{ formatTime(audioDuration) }}</span>
                                </div>
                            </div>

                            <div class="segment-timeline mt-3">
                                <div class="d-flex align-items-center justify-content-between mb-1">
                                    <label class="small mb-0" style="color: #525f7f; font-weight: 600;">Segment Timeline (drag handles to adjust)</label>
                                    <div class="timeline-zoom-controls">
                                        <button class="btn btn-sm btn-outline-secondary py-0 px-1" @click="timelineZoomOut" :disabled="timelineZoom <= 1" title="Zoom out">
                                            <i class="fa fa-minus"></i>
                                        </button>
                                        <span class="small text-muted mx-1">{{ timelineZoom }}x</span>
                                        <button class="btn btn-sm btn-outline-secondary py-0 px-1" @click="timelineZoomIn" :disabled="timelineZoom >= 20" title="Zoom in">
                                            <i class="fa fa-plus"></i>
                                        </button>
                                        <button v-if="timelineZoom > 1" class="btn btn-sm btn-outline-secondary py-0 px-1 ml-1" @click="timelineZoom = 1" title="Reset zoom">
                                            <i class="fa fa-compress"></i>
                                        </button>
                                    </div>
                                </div>
                                <div class="timeline-container" ref="timelineContainer" :class="{ 'timeline-scrollable': timelineZoom > 1 }">
                                    <div class="timeline-track" @click="seekFromTimeline($event)" :style="{ width: (100 * timelineZoom) + '%' }">
                                        <div class="timeline-playhead" :style="{ left: playbackProgress + '%' }"></div>
                                        <div v-for="(segment, idx) in segments" :key="idx"
                                             class="timeline-segment"
                                             :style="getSegmentStyle(segment)"
                                             :class="{ active: currentSegmentIndex === idx }"
                                             @mousedown.stop="startTimelineDrag(idx, 'move', $event)"
                                             @touchstart.stop.prevent="startTimelineDrag(idx, 'move', $event)">
                                            <div class="segment-handle left" 
                                                 @mousedown.stop="startTimelineDrag(idx, 'start', $event)"
                                                 @touchstart.stop.prevent="startTimelineDrag(idx, 'start', $event)"
                                                 :title="'Start: ' + formatTime(segment.startTime)"></div>
                                            <div class="segment-label">{{ segment.ayah === 0 ? 'B' : segment.ayah }}</div>
                                            <div class="segment-handle right"
                                                 @mousedown.stop="startTimelineDrag(idx, 'end', $event)"
                                                 @touchstart.stop.prevent="startTimelineDrag(idx, 'end', $event)"
                                                 :title="'End: ' + formatTime(segment.endTime)"></div>
                                        </div>
                                    </div>
                                </div>
                                <div class="timeline-times d-flex justify-content-between mt-1">
                                    <small class="text-muted">0:00</small>
                                    <small v-if="timelineZoom > 1" class="text-muted">{{ formatTime(audioDuration / 2) }}</small>
                                    <small class="text-muted">{{ formatTime(audioDuration) }}</small>
                                </div>
                            </div>
                        </div>

                        <div class="col-md-5">
                            <div class="settings-panel bg-white rounded p-3">
                                <div class="section-header" @click="expandedSections.videoSettings = !expandedSections.videoSettings">
                                    <h5 class="mb-0 d-flex align-items-center justify-content-between">
                                        Video Settings
                                        <i class="fa" :class="expandedSections.videoSettings ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                    </h5>
                                </div>
                                <div v-show="expandedSections.videoSettings" class="section-content mt-3">
                                <div class="form-group">
                                    <label>Aspect Ratio</label>
                                    <div class="btn-group w-100">
                                        <base-button :type="aspectRatio === '16:9' ? 'primary' : 'secondary'" @click="setAspectRatio('16:9')">16:9</base-button>
                                        <base-button :type="aspectRatio === '9:16' ? 'primary' : 'secondary'" @click="setAspectRatio('9:16')">9:16</base-button>
                                        <base-button :type="aspectRatio === '1:1' ? 'primary' : 'secondary'" @click="setAspectRatio('1:1')">1:1</base-button>
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label>Background Type</label>
                                    <div class="btn-group w-100 mb-2">
                                        <base-button :type="backgroundType === 'color' ? 'primary' : 'secondary'" size="sm" @click="backgroundType = 'color'">Color</base-button>
                                        <base-button :type="backgroundType === 'image' ? 'primary' : 'secondary'" size="sm" @click="backgroundType = 'image'">Image</base-button>
                                        <base-button :type="backgroundType === 'video' ? 'primary' : 'secondary'" size="sm" @click="backgroundType = 'video'">Video</base-button>
                                        <base-button :type="backgroundType === 'transparent' ? 'primary' : 'secondary'" size="sm" @click="backgroundType = 'transparent'">Transparent</base-button>
                                    </div>
                                    
                                    <div v-if="backgroundType === 'transparent'" class="alert alert-success small py-2">
                                        <i class="fa fa-info-circle"></i> Exports as WebM with alpha channel. Use as overlay in video editors.
                                    </div>
                                    
                                    <div v-if="backgroundType === 'color'">
                                        <input type="color" v-model="backgroundColor" class="form-control">
                                    </div>
                                    
                                    <div v-if="backgroundType === 'image'">
                                        <label class="small text-muted">Presets</label>
                                        <div class="preset-grid mb-2">
                                            <div v-for="(img, idx) in presetImages" :key="'img-'+idx" 
                                                 class="preset-thumb" 
                                                 :class="{ active: selectedPreset === img }"
                                                 @click="selectPresetImage(img)">
                                                <img :src="img" alt="Preset">
                                            </div>
                                        </div>
                                        <input type="file" ref="bgImageInput" @change="handleBgImage" accept="image/*" style="display: none">
                                        <base-button type="secondary" size="sm" class="w-100" @click="$refs.bgImageInput.click()">
                                            Upload Custom Image
                                        </base-button>
                                    </div>
                                    
                                    <div v-if="backgroundType === 'video'">
                                        <label class="small text-muted">Presets</label>
                                        <div class="preset-grid mb-2">
                                            <div v-for="(vid, idx) in presetVideos" :key="'vid-'+idx" 
                                                 class="preset-thumb video-thumb" 
                                                 :class="{ active: selectedPresetVideo === vid.url }"
                                                 @click="selectPresetVideo(vid)">
                                                <img :src="vid.thumb" alt="Video">
                                                <i class="fa fa-play-circle"></i>
                                            </div>
                                        </div>
                                        <input type="file" ref="bgVideoInput" @change="handleBgVideo" accept="video/*" style="display: none">
                                        <base-button type="secondary" size="sm" class="w-100" @click="$refs.bgVideoInput.click()">
                                            Upload Custom Video
                                        </base-button>
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label>Font Style</label>
                                    <base-dropdown class="w-100">
                                        <base-button slot="title" outline type="primary" class="dropdown-toggle w-100">
                                            {{ selectedFont.name }}
                                        </base-button>
                                        <base-button class="dropdown-item" 
                                            @click="selectFont(font)" 
                                            v-for="font in fontOptions" 
                                            :key="font.name">
                                            {{ font.name }}
                                        </base-button>
                                    </base-dropdown>
                                </div>

                                <div class="form-group">
                                    <label>Arabic Font Size: {{ arabicFontSize }}px</label>
                                    <input type="range" v-model="arabicFontSize" min="15" max="80" class="form-control-range w-100">
                                </div>
                                
                                <div class="form-group">
                                    <label>Line Spacing: {{ arabicLineHeight.toFixed(1) }}x</label>
                                    <input type="range" v-model.number="arabicLineHeight" min="1.2" max="3.0" step="0.1" class="form-control-range w-100">
                                </div>

                                <div class="form-group">
                                    <label>Arabic Text Color</label>
                                    <input type="color" v-model="arabicTextColor" class="form-control">
                                </div>
                                
                                <div class="form-group mt-3">
                                    <div class="custom-control custom-checkbox">
                                        <input type="checkbox" class="custom-control-input" id="showTextBackground" v-model="showTextBackground">
                                        <label class="custom-control-label" for="showTextBackground">Show Text Background</label>
                                    </div>
                                </div>
                                
                                <div v-if="showTextBackground" class="form-group">
                                    <label>Background Color</label>
                                    <input type="color" v-model="textBackgroundColor" class="form-control">
                                </div>
                                
                                <div v-if="showTextBackground" class="form-group">
                                    <label>Background Opacity: {{ textBackgroundOpacity }}%</label>
                                    <input type="range" v-model="textBackgroundOpacity" min="10" max="90" class="custom-range">
                                </div>
                                
                                <div class="form-group mt-3">
                                    <div class="custom-control custom-checkbox">
                                        <input type="checkbox" class="custom-control-input" id="showTextShadow" v-model="showTextShadow">
                                        <label class="custom-control-label" for="showTextShadow">Show Text Shadow</label>
                                    </div>
                                </div>
                                
                                <div v-if="showTextShadow" class="form-group">
                                    <label>Shadow Color</label>
                                    <input type="color" v-model="textShadowColor" class="form-control">
                                </div>
                                
                                <div v-if="showTextShadow" class="form-group">
                                    <label>Horizontal Offset: {{ textShadowOffsetX }}px</label>
                                    <input type="range" v-model.number="textShadowOffsetX" min="-10" max="10" class="custom-range">
                                </div>
                                
                                <div v-if="showTextShadow" class="form-group">
                                    <label>Vertical Offset: {{ textShadowOffsetY }}px</label>
                                    <input type="range" v-model.number="textShadowOffsetY" min="-10" max="10" class="custom-range">
                                </div>
                                
                                <div v-if="showTextShadow" class="form-group">
                                    <label>Shadow Blur: {{ textShadowBlur }}px</label>
                                    <input type="range" v-model.number="textShadowBlur" min="0" max="15" class="custom-range">
                                </div>
                                </div>

                                <hr>
                                <div class="section-header" @click="expandedSections.overlays = !expandedSections.overlays">
                                    <h5 class="mb-0 d-flex align-items-center justify-content-between">
                                        Overlays
                                        <i class="fa" :class="expandedSections.overlays ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                    </h5>
                                </div>
                                <div v-show="expandedSections.overlays" class="section-content mt-3">
                                <div class="form-group">
                                    <base-checkbox v-model="showBismillah">Show Bismillah at Start</base-checkbox>
                                </div>
                                
                                <div v-if="showBismillah" class="form-group">
                                    <label>Bismillah Style</label>
                                    <select v-model="bismillahType" class="form-control">
                                        <option value="arabic">Arabic Text</option>
                                        <option value="logo">Bismillah Logo</option>
                                    </select>
                                </div>
                                
                                <div v-if="showBismillah && bismillahType === 'logo'" class="form-group">
                                    <label>Logo Color</label>
                                    <input type="color" v-model="bismillahLogoColor" class="form-control">
                                </div>
                                
                                <div class="form-group">
                                    <base-checkbox v-model="showAyahNumber">Show Ayah Number</base-checkbox>
                                </div>
                                
                                <div v-if="showAyahNumber" class="form-group">
                                    <label>Ayah Marker Font</label>
                                    <select v-model="ayahMarkerFont" class="form-control">
                                        <option v-for="font in ayahMarkerFonts" :key="font.id" :value="font.id">{{ font.name }}</option>
                                    </select>
                                </div>
                                
                                <div v-if="showAyahNumber" class="form-group">
                                    <label>Ayah Marker Size: {{ ayahMarkerSize }}%</label>
                                    <input type="range" v-model.number="ayahMarkerSize" min="50" max="150" class="custom-range">
                                </div>
                                
                                <div class="form-group">
                                    <base-checkbox v-model="showSurahName">Show Surah Name</base-checkbox>
                                </div>
                                
                                <div v-if="showSurahName" class="form-group">
                                    <label>Surah Name Style</label>
                                    <select v-model="surahNameType" class="form-control">
                                        <option value="arabic">Arabic Text</option>
                                        <option value="logo">Surah Logo Image</option>
                                    </select>
                                </div>
                                
                                <div v-if="showSurahName && surahNameType !== 'logo'" class="form-group">
                                    <label>Surah Name Size: {{ surahNameSize }}px</label>
                                    <input type="range" v-model.number="surahNameSize" min="15" max="60" class="form-control-range w-100">
                                    <small class="text-muted">Drag surah name on preview to reposition</small>
                                </div>
                                
                                <div v-if="showSurahName && surahNameType === 'logo'" class="form-group">
                                    <label>Logo Size: {{ surahNameSize }}px</label>
                                    <input type="range" v-model.number="surahNameSize" min="15" max="150" class="form-control-range w-100">
                                    <small class="text-muted">Drag logo on preview to reposition</small>
                                </div>
                                
                                <div v-if="showSurahName && surahNameType !== 'logo'" class="form-group">
                                    <label>Surah Name Color</label>
                                    <input type="color" v-model="surahNameColor" class="form-control">
                                </div>
                                
                                <div v-if="showSurahName && surahNameType === 'logo'" class="form-group">
                                    <label>Logo Color</label>
                                    <input type="color" v-model="surahLogoColor" class="form-control">
                                </div>
                                
                                <div v-if="showSurahName" class="form-group mt-2">
                                    <base-checkbox v-model="showEnglishSurahName">Show English Name</base-checkbox>
                                </div>
                                
                                <div v-if="showSurahName && showEnglishSurahName" class="form-group">
                                    <label>English Name Size: {{ englishSurahNameSize }}px</label>
                                    <input type="range" v-model.number="englishSurahNameSize" min="12" max="40" class="form-control-range w-100">
                                </div>
                                
                                <div v-if="showSurahName && showEnglishSurahName" class="form-group">
                                    <label>English Name Color</label>
                                    <input type="color" v-model="englishSurahNameColor" class="form-control">
                                </div>
                                
                                <div class="form-group">
                                    <base-checkbox v-model="showTranslation">Show Translation</base-checkbox>
                                </div>
                                </div>

                                <hr>
                                <div class="section-header" @click="expandedSections.logo = !expandedSections.logo">
                                    <h5 class="mb-0 d-flex align-items-center justify-content-between">
                                        Add Logo
                                        <i class="fa" :class="expandedSections.logo ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                    </h5>
                                </div>
                                <div v-show="expandedSections.logo" class="section-content mt-3">
                                <div class="form-group">
                                    <input type="file" ref="clientLogoInput" @change="handleClientLogo" accept="image/*" style="display: none">
                                    <div v-if="clientLogo" class="client-logo-preview mb-2">
                                        <img :src="clientLogo" alt="Client Logo" style="max-height: 40px;">
                                        <base-button type="danger" size="sm" class="ml-2" @click="removeClientLogo">
                                            <i class="fa fa-times"></i>
                                        </base-button>
                                    </div>
                                    <base-button type="secondary" size="sm" class="w-100" @click="$refs.clientLogoInput.click()">
                                        {{ clientLogo ? 'Change Logo' : 'Upload Your Logo' }}
                                    </base-button>
                                </div>

                                <div v-if="clientLogo" class="form-group">
                                    <label>Logo Size: {{ clientLogoSize }}px</label>
                                    <input type="range" v-model.number="clientLogoSize" min="30" max="200" class="form-control-range w-100">
                                    <small class="text-muted">Drag logo on preview to reposition</small>
                                </div>
                                </div>

                                <div v-if="showTranslation">
                                <hr>
                                <div class="section-header" @click="expandedSections.translation = !expandedSections.translation">
                                    <h5 class="mb-0 d-flex align-items-center justify-content-between">
                                        Translation Settings
                                        <i class="fa" :class="expandedSections.translation ? 'fa-chevron-up' : 'fa-chevron-down'"></i>
                                    </h5>
                                </div>
                                <div v-show="expandedSections.translation" class="section-content mt-3">
                                    <div class="form-group">
                                        <label>Translation Language</label>
                                        <base-dropdown class="w-100">
                                            <base-button slot="title" outline type="primary" class="dropdown-toggle w-100 text-truncate">
                                                {{ selectedTranslation.language_name || 'Select Language' }}
                                            </base-button>
                                            <base-button class="dropdown-item" 
                                                @click="selectTranslation(trans)" 
                                                v-for="trans in translations" 
                                                :key="trans.id">
                                                {{ trans.language_name }}
                                            </base-button>
                                        </base-dropdown>
                                    </div>

                                    <div class="form-group">
                                        <label>Translation Font Size: {{ translationFontSize }}px</label>
                                        <input type="range" v-model="translationFontSize" min="12" max="40" class="form-control-range w-100">
                                    </div>

                                    <div class="form-group">
                                        <label>Translation Text Color</label>
                                        <input type="color" v-model="translationTextColor" class="form-control">
                                    </div>
                                </div>
                                </div>

                                <hr>
                                <div class="text-center">
                                    <!-- Queue Status Display -->
                                    <div v-if="!isExporting" class="mb-3">
                                        <div v-if="queueStatus" :class="['alert', 'py-2', 'px-3', 'd-inline-block', queueStatus.available_slots > 0 ? 'alert-success' : 'alert-warning']" style="border-radius: 8px;">
                                            <i class="fa fa-server mr-1"></i>
                                            <strong>Server Queue:</strong> 
                                            {{ queueStatus.queue_size }}/{{ queueStatus.max_queue_size }} jobs
                                            <span v-if="queueStatus.available_slots > 0">
                                                - <strong>{{ queueStatus.available_slots }}</strong> slots available
                                            </span>
                                            <span v-else>
                                                - Queue full, please wait
                                            </span>
                                            <base-button type="link" size="sm" @click="fetchQueueStatus" :disabled="queueLoading" class="ml-2 p-0" style="vertical-align: baseline;">
                                                <i :class="queueLoading ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
                                            </base-button>
                                        </div>
                                        <div v-else class="alert alert-light py-2 px-3 d-inline-block" style="border-radius: 8px;">
                                            <i :class="queueLoading ? 'fa fa-spinner fa-spin' : 'fa fa-server'" class="mr-1"></i>
                                            <span v-if="queueLoading">Loading queue status...</span>
                                            <span v-else>
                                                Queue status unavailable
                                                <base-button type="link" size="sm" @click="fetchQueueStatus" class="ml-2 p-0" style="vertical-align: baseline;">
                                                    <i class="fa fa-refresh"></i>
                                                </base-button>
                                            </span>
                                        </div>
                                    </div>
                                    <base-button type="success" size="lg" @click="exportVideo" :disabled="isExporting || (queueStatus && queueStatus.available_slots <= 0)">
                                        <span v-if="isExporting">
                                            <i class="fa fa-spinner fa-spin"></i> {{ jobStage || 'Processing' }}... {{ exportProgress }}%
                                        </span>
                                        <span v-else-if="queueStatus && queueStatus.available_slots <= 0">
                                            <i class="fa fa-clock-o"></i> Queue Full - Please Wait
                                        </span>
                                        <span v-else>
                                            <i class="fa fa-download"></i> Export Video
                                        </span>
                                    </base-button>
                                    <div v-if="isExporting" class="mt-3">
                                        <div class="progress" style="height: 20px;">
                                            <div class="progress-bar bg-success progress-bar-striped progress-bar-animated" 
                                                 role="progressbar" 
                                                 :style="{ width: exportProgress + '%' }"
                                                 :aria-valuenow="exportProgress" 
                                                 aria-valuemin="0" 
                                                 aria-valuemax="100">
                                            </div>
                                        </div>
                                        <small class="text-muted mt-2 d-block">{{ exportElapsedTime }}</small>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import surahs from '../data/surahList.json';

export default {
    name: 'VideoCreator',
    data() {
        return {
            currentStep: 1,
            isDragging: false,
            isSeeking: false,
            
            expandedSections: {
                videoSettings: true,
                overlays: false,
                logo: false,
                translation: false
            },
            audioFile: null,
            audioUrl: null,
            isAnalyzing: false,
            analysisError: null,
            
            detectedSurah: 0,
            segments: [],
            surahArray: surahs,
            maxAyahs: 7,
            
            fontOptions: [
                { id: 'amiri', name: 'Amiri Quran', field: 'text_uthmani', wordField: 'text_uthmani', cssFont: "'Amiri Quran', 'Amiri', serif", backendFont: 'amiri' },
                { id: 'scheherazade', name: 'Scheherazade New', field: 'text_uthmani', wordField: 'text_uthmani', cssFont: "'Scheherazade New', serif", backendFont: 'scheherazade' },
                { id: 'kfgqpc', name: 'Uthmanic Script', field: 'text_uthmani', wordField: 'text_uthmani', cssFont: "'KFGQPC Uthmanic Script HAFS', serif", backendFont: 'kfgqpc' },
                { id: 'pdms', name: 'Saleem Quran', field: 'text_uthmani', wordField: 'text_uthmani', cssFont: "'_PDMS_Saleem_QuranFont', serif", backendFont: 'pdms' },
                { id: 'indopak', name: 'IndoPak Nastaleeq', field: 'text_indopak', wordField: 'text_indopak', cssFont: "'Noto Nastaliq Urdu', serif", backendFont: 'indopak' }
            ],
            selectedFont: { id: 'kfgqpc', name: 'Uthmanic Script', field: 'text_uthmani', wordField: 'text_uthmani', cssFont: "'KFGQPC Uthmanic Script HAFS', serif", backendFont: 'kfgqpc' },
            currentSegmentIndex: -1,
            
            translations: [
                { id: 1, language_name: 'English', name: 'Word by Word' },
                { id: 2, language_name: 'Urdu', name: 'Word by Word', font: 'Urdu', rtl: true },
                { id: 3, language_name: 'Hindi', name: 'Word by Word' },
                { id: 4, language_name: 'Indonesian', name: 'Word by Word' },
                { id: 5, language_name: 'Bangla', name: 'Word by Word' },
                { id: 6, language_name: 'Turkish', name: 'Word by Word' },
                { id: 7, language_name: 'Tamil', name: 'Word by Word' },
                { id: 8, language_name: 'German', name: 'Word by Word' },
                { id: 11, language_name: 'French', name: 'Word by Word' },
                { id: 12, language_name: 'Malayalam (Amani)', name: 'Word by Word' },
                { id: 13, language_name: 'Malayalam (Lalithasaram)', name: 'Word by Word' },
                { id: 14, language_name: 'Chinese (Traditional)', name: 'Word by Word' },
                { id: 15, language_name: 'Chinese (Zhuyin)', name: 'Word by Word' },
                { id: 16, language_name: 'Chinese (Simplified)', name: 'Word by Word' },
                { id: 17, language_name: 'Chinese (Pinyin)', name: 'Word by Word' },
                { id: 18, language_name: 'Divehi', name: 'Word by Word', rtl: true },
                { id: 19, language_name: 'Persian', name: 'Word by Word', font: 'Urdu', rtl: true },
                { id: 20, language_name: 'Sindhi', name: 'Word by Word', font: 'Sindhi', rtl: true },
                { id: 21, language_name: 'Albanian', name: 'Word by Word' }
            ],
            selectedTranslation: { id: 1, language_name: 'English', name: 'Word by Word' },
            wbwTranslationCache: {},
            
            aspectRatio: '16:9',
            videoWidth: 640,
            videoHeight: 360,
            backgroundColor: '#172b4d',
            backgroundType: 'color',
            backgroundImage: null,
            backgroundVideo: null,
            backgroundVideoFile: null,
            selectedPreset: null,
            selectedPresetVideo: null,
            presetImages: [
                '/backgrounds/peaceful_nature_land_3813f16e.jpg',
                '/backgrounds/peaceful_nature_land_91a22188.jpg',
                '/backgrounds/peaceful_nature_land_1ae54f50.jpg'
            ],
            presetVideos: [
                { url: '/backgrounds/videos/nature1.mp4', preview: '/backgrounds/videos/nature1_preview.mp4', thumb: '/backgrounds/videos/nature1_thumb.jpg', name: 'Nature 1' },
                { url: '/backgrounds/videos/nature2.mp4', preview: '/backgrounds/videos/nature2_preview.mp4', thumb: '/backgrounds/videos/nature2_thumb.jpg', name: 'Nature 2' },
                { url: '/backgrounds/videos/nature3.mp4', preview: '/backgrounds/videos/nature3_preview.mp4', thumb: '/backgrounds/videos/nature3_thumb.jpg', name: 'Nature 3' }
            ],
            
            arabicFontSize: 36,
            prevArabicFontSize: 36,
            arabicLineHeight: 1.6,
            arabicTextColor: '#ffffff',
            arabicPosition: { x: 50, y: 40 },
            arabicWidth: 80,
            arabicHeight: 30,
            
            showTextBackground: false,
            textBackgroundColor: '#000000',
            textBackgroundOpacity: 50,
            
            showTextShadow: true,
            textShadowColor: '#000000',
            textShadowOffsetX: 2,
            textShadowOffsetY: 3,
            textShadowBlur: 4,
            
            showTranslation: true,
            showBismillah: false,
            bismillahType: 'arabic',
            bismillahLogoUrl: null,
            bismillahLogoColor: '#ffffff',
            showAyahNumber: true,
            ayahMarkerFont: 'uthmani',
            ayahMarkerFonts: [
                { id: 'uthmani', name: 'Uthmani', cssFont: "'KFGQPC Uthmanic Script HAFS', 'Amiri', serif" },
                { id: 'amiri', name: 'Amiri Quran', cssFont: "'Amiri Quran', 'Amiri', serif" },
                { id: 'scheherazade', name: 'Scheherazade', cssFont: "'Scheherazade New', serif" }
            ],
            ayahMarkerSize: 100,
            translationFontSize: 18,
            translationTextColor: '#cccccc',
            translationPosition: { x: 50, y: 70 },
            translationWidth: 70,
            translationHeight: 20,
            
            clientLogo: null,
            clientLogoFile: null,
            clientLogoSize: 60,
            clientLogoPosition: { x: 15, y: 85 },
            
            showSurahName: false,
            surahNameType: 'logo',
            surahNameSize: 28,
            surahNamePosition: { x: 50, y: 10 },
            surahNameColor: '#ffffff',
            surahLogoColor: '#ffffff',
            arabicSurahName: '',
            englishSurahName: '',
            surahLogoUrl: null,
            colorizedSurahLogoUrl: null,
            showEnglishSurahName: false,
            englishSurahNameSize: 18,
            englishSurahNamePosition: { x: 50, y: 15 },
            englishSurahNameColor: '#cccccc',
            
            watermarkSize: 40,
            watermarkPosition: { x: 95, y: 95 },
            
            resizeTarget: null,
            resizeDirection: null,
            
            isPlaying: false,
            currentTime: 0,
            audioDuration: 0,
            playbackProgress: 0,
            timelineZoom: 1,
            
            showOverlayControls: true,
            dragTarget: null,
            dragOffset: { x: 0, y: 0 },
            draggingSegment: null,
            
            isExporting: false,
            exportProgress: 0,
            exportStartTime: null,
            exportElapsedSeconds: 0,
            exportTimer: null,
            
            apiBaseUrl: '',
            apiToken: null,
            
            jobId: null,
            jobStatus: null,
            jobStage: '',
            jobPollingInterval: null,
            
            queueStatus: null,
            queueLoading: false,
            
            cachedBackgroundImage: null,
            cachedBackgroundVideo: null,
            cachedBackgroundSrc: null,
            drawPreviewPending: false,
            videoAnimationId: null
        }
    },
    computed: {
        currentArabicText() {
            const segment = this.getCurrentSegment();
            if (!segment) return '';
            return segment.arabicText || segment.fullArabicText || '';
        },
        currentTranslation() {
            const segment = this.getCurrentSegment();
            if (!segment) return '';
            return segment.translationText || '';
        },
        currentSurahName() {
            return this.arabicSurahName || '';
        },
        exportElapsedTime() {
            const mins = Math.floor(this.exportElapsedSeconds / 60);
            const secs = this.exportElapsedSeconds % 60;
            if (mins > 0) {
                return `${mins}m ${secs}s`;
            }
            return `${secs}s`;
        },
        selectedMarkerFontCss() {
            const font = this.ayahMarkerFonts.find(f => f.id === this.ayahMarkerFont);
            return font ? font.cssFont : "'Amiri Quran', 'Amiri', serif";
        }
    },
    async mounted() {
        await this.setApiBaseUrl();
        this.loadFonts();
        this.fetchQueueStatus();
    },
    methods: {
        toArabicNumber(num) {
            const arabicDigits = ['٠', '١', '٢', '٣', '٤', '٥', '٦', '٧', '٨', '٩'];
            return String(num).split('').map(d => arabicDigits[parseInt(d)] || d).join('');
        },
        
        getAyahMarker(ayahNum) {
            if (this.ayahMarkerFont === 'uthmani') {
                return ' ' + this.toArabicNumber(ayahNum);
            }
            return ' ﴿' + this.toArabicNumber(ayahNum) + '﴾';
        },
        
        shouldShowAyahMarker(segment) {
            if (!segment || segment.ayah === 0 || segment.ayah === 'bismillah') return false;
            if (!this.showAyahNumber) return false;
            
            const words = segment.words || [];
            const endWord = typeof segment.endWord === 'number' ? segment.endWord : (words.length - 1);
            const isLastWordOfAyah = endWord >= words.length - 1;
            
            if (isLastWordOfAyah) return true;
            
            const sameAyahSegments = this.segments.filter(s => s.surah === segment.surah && s.ayah === segment.ayah);
            if (sameAyahSegments.length > 1) {
                const sortedByTime = [...sameAyahSegments].sort((a, b) => a.startTime - b.startTime);
                const isLastSegmentOfAyah = sortedByTime[sortedByTime.length - 1] === segment;
                return isLastSegmentOfAyah;
            }
            
            return true;
        },
        
        getArabicPreviewStyle() {
            return {
                fontFamily: this.selectedFont ? this.selectedFont.cssFont : "'Amiri', serif",
                fontSize: '1.4rem',
                lineHeight: '2'
            };
        },
        getWordPillStyle() {
            return {
                fontFamily: this.selectedFont ? this.selectedFont.cssFont : "'Amiri', serif",
                display: 'inline-block',
                padding: '4px 8px',
                margin: '3px',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '1.1rem',
                lineHeight: '1.8'
            };
        },
        async setApiBaseUrl() {
            this.apiBaseUrl = '';
            await this.refreshApiToken();
        },
        async refreshApiToken() {
            try {
                const response = await this.axios.get(`${this.apiBaseUrl}/api/token`);
                if (response.data && response.data.token) {
                    this.apiToken = response.data.token;
                    this._tokenFetchedAt = Date.now();
                }
            } catch (e) {
                this.apiToken = null;
            }
        },
        async getValidToken() {
            const TOKEN_REFRESH_MS = 50 * 60 * 1000;
            if (!this.apiToken || !this._tokenFetchedAt || (Date.now() - this._tokenFetchedAt) > TOKEN_REFRESH_MS) {
                await this.refreshApiToken();
            }
            return this.apiToken;
        },
        async apiRequest(method, url, data, extraConfig) {
            await this.getValidToken();
            const headers = {};
            if (this.apiToken) {
                headers['x-api-token'] = this.apiToken;
            }
            const adminToken = localStorage.getItem('adminToken');
            if (adminToken) {
                headers['x-admin-token'] = adminToken;
            }
            const config = { headers, ...extraConfig };
            try {
                if (method === 'get') {
                    return await this.axios.get(url, config);
                } else {
                    return await this.axios.post(url, data, config);
                }
            } catch (e) {
                if (e.response && e.response.status === 401) {
                    await this.refreshApiToken();
                    if (this.apiToken) {
                        config.headers['x-api-token'] = this.apiToken;
                        if (method === 'get') {
                            return await this.axios.get(url, config);
                        } else {
                            return await this.axios.post(url, data, config);
                        }
                    }
                }
                throw e;
            }
        },
        
        async fetchQueueStatus() {
            this.queueLoading = true;
            try {
                const response = await this.apiRequest('get', `${this.apiBaseUrl}/api/queue/status`);
                if (response.data && response.data.success) {
                    this.queueStatus = response.data.queue;
                }
            } catch (e) {
                // Queue status fetch failed silently
            } finally {
                this.queueLoading = false;
            }
        },
        
        async loadFonts() {
            const fontsToLoad = [
                'Amiri',
                'Amiri Quran',
                'Scheherazade New',
                'Noto Nastaliq Urdu',
                'KFGQPC Uthmanic Script HAFS',
                '_PDMS_Saleem_QuranFont',
                'QPC Hafs V1',
                'QPC Hafs V2',
                'QPC Hafs V4',
                'Hafs Nastaleeq'
            ];
            
            try {
                if (document.fonts && document.fonts.ready) {
                    await document.fonts.ready;
                    
                    for (const fontName of fontsToLoad) {
                        try {
                            await document.fonts.load(`36px "${fontName}"`);
                        } catch (e) {
                            // Font not available
                        }
                    }
                }
            } catch (e) {
                // Font loading error
            }
            
            this.$nextTick(() => this.drawPreview());
        },
        
        async ensureFontLoaded(font) {
            if (!font || !font.cssFont) return;
            
            const fontFamily = font.cssFont.split(',')[0].replace(/['"]/g, '').trim();
            
            try {
                if (document.fonts && document.fonts.check) {
                    const isLoaded = document.fonts.check(`36px "${fontFamily}"`);
                    if (!isLoaded) {
                        await document.fonts.load(`36px "${fontFamily}"`);
                    }
                }
            } catch (e) {
                // Font load error
            }
        },
        
        formatFileSize(bytes) {
            if (bytes < 1024) return bytes + ' B';
            if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
            return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
        },
        
        formatTime(seconds) {
            const mins = Math.floor(seconds / 60);
            const secs = Math.floor(seconds % 60);
            return `${mins}:${secs.toString().padStart(2, '0')}`;
        },
        
        removeArabicDiacritics(text) {
            if (!text) return '';
            return text.replace(/[\u064B-\u065F\u0670]/g, '');
        },
        
        handleDrop(e) {
            this.isDragging = false;
            const files = e.dataTransfer.files;
            if (files.length > 0) {
                this.processFile(files[0]);
            }
        },
        
        handleFileSelect(e) {
            const files = e.target.files;
            if (files.length > 0) {
                this.processFile(files[0]);
            }
        },
        
        processFile(file) {
            if (!file.type.startsWith('audio/')) {
                this.$notify({
                    message: 'Please select an audio file',
                    type: 'warning'
                });
                return;
            }
            
            const maxSize = 25 * 1024 * 1024;
            if (file.size > maxSize) {
                this.$notify({
                    message: 'Audio file must be under 25MB',
                    type: 'danger'
                });
                return;
            }
            
            this.audioFile = file;
            this.audioUrl = URL.createObjectURL(file);
            
            this.$nextTick(() => {
                if (this.$refs.audioPlayer) {
                    this.$refs.audioPlayer.onloadedmetadata = () => {
                        const duration = this.$refs.audioPlayer.duration;
                        const isAdmin = !!localStorage.getItem('adminToken');
                        if (!isAdmin && duration > 300) {
                            this.$notify({
                                message: 'Audio must be 5 minutes or less',
                                type: 'danger'
                            });
                            this.removeFile();
                            return;
                        }
                        this.audioDuration = duration;
                    };
                }
            });
        },
        
        removeFile() {
            if (this.audioUrl) {
                URL.revokeObjectURL(this.audioUrl);
            }
            this.audioFile = null;
            this.audioUrl = null;
        },
        
        async analyzeAudio() {
            if (!this.audioFile) return;
            
            this.isAnalyzing = true;
            this.analysisError = null;
            
            try {
                const formData = new FormData();
                formData.append('audio', this.audioFile);
                
                const response = await this.apiRequest('post', `${this.apiBaseUrl}/api/analyze`, formData);
                
                const data = response.data;
                
                if (data.success) {
                    this.detectedSurah = data.surah || 1;
                    this.maxAyahs = parseInt(this.surahArray[this.detectedSurah - 1].ayas);
                    this.fetchArabicSurahName(this.detectedSurah);
                    this.segments = (data.segments || []).map(seg => {
                        let startTime = parseFloat(seg.startTime) || 0;
                        let endTime = parseFloat(seg.endTime) || startTime + 2;
                        const audioDur = Math.max(this.audioDuration || 30, 1);
                        const minDuration = Math.min(0.5, audioDur);
                        
                        if (startTime < 0) startTime = 0;
                        if (startTime > audioDur - minDuration) startTime = Math.max(0, audioDur - minDuration);
                        if (endTime <= startTime) endTime = startTime + minDuration;
                        if (endTime > audioDur) endTime = audioDur;
                        if (endTime <= startTime) {
                            startTime = 0;
                            endTime = audioDur;
                        }
                        
                        // Convert 1-based wordStart/wordEnd from API to 0-based indices for frontend
                        // Coerce to numbers first since JSON may deliver them as strings
                        const apiWordStart = Number(seg.wordStart);
                        const apiWordEnd = Number(seg.wordEnd);
                        const hasWordRange = Number.isFinite(apiWordStart) && Number.isFinite(apiWordEnd) && apiWordStart > 0 && apiWordEnd > 0;
                        const startWord = hasWordRange ? Math.max(0, apiWordStart - 1) : 0;
                        const endWord = hasWordRange ? Math.max(0, apiWordEnd - 1) : -1;
                        
                        console.log(`Segment ayah ${seg.ayah}: API words=${seg.wordStart}-${seg.wordEnd}, hasRange=${hasWordRange}, converted=${startWord}-${endWord}`);
                        
                        return {
                            ...seg,
                            startTime,
                            endTime,
                            arabicText: '',
                            fullArabicText: '',
                            translationText: '',
                            words: [],
                            startWord: startWord,
                            endWord: endWord,
                            hasAiWordRange: hasWordRange,
                            useManualText: false,
                            manualText: ''
                        };
                    });
                    
                    await Promise.all(this.segments.map(segment => this.fetchAyahText(segment)));
                    
                    this.segments = this.segments.slice();
                    this.$forceUpdate();
                    this.$nextTick(() => {
                        this.drawPreview();
                    });
                    this.currentStep = 2;
                    
                    if (typeof window.gtag === 'function') {
                        window.gtag('event', 'audio_analysis', {
                            event_category: 'video_creator',
                            surah: this.detectedSurah,
                            ayah_count: this.segments.length,
                            audio_duration: Math.round(this.audioDuration)
                        });
                    }
                } else {
                    this.analysisError = data.error || 'Analysis failed';
                }
            } catch (error) {
                console.error('Analysis error:', error);
                let errorMessage = 'Failed to analyze audio. Please try again.';
                
                if (error.response) {
                    const status = error.response.status;
                    const data = error.response.data;
                    
                    if (data && data.error) {
                        errorMessage = data.error;
                    } else if (status === 400) {
                        errorMessage = 'Invalid audio file. Please ensure you are uploading a valid Quran recitation audio file (MP3 or WAV).';
                    } else if (status === 413) {
                        errorMessage = 'Audio file is too large. Please upload a file under 25MB.';
                    } else if (status === 429) {
                        errorMessage = 'Too many requests. Please wait a moment and try again.';
                    } else if (status === 500) {
                        errorMessage = 'Server error occurred. Please try again later or contact support if the issue persists.';
                    } else if (status === 503) {
                        errorMessage = 'Service temporarily unavailable. Please try again in a few moments.';
                    }
                } else if (error.message) {
                    if (error.message.includes('Network Error')) {
                        errorMessage = 'Network connection error. Please check your internet connection and try again.';
                    } else if (error.message.includes('timeout')) {
                        errorMessage = 'Request timed out. Please try again with a shorter audio file.';
                    }
                }
                
                this.analysisError = errorMessage;
            } finally {
                this.isAnalyzing = false;
            }
        },
        
        async fetchAyahText(segment) {
            try {
                const wordField = this.selectedFont?.wordField || 'text_uthmani';
                const fontField = this.selectedFont?.field || 'text_uthmani';
                
                if (segment.ayah === 0) {
                    segment.isBismillah = true;
                    segment.useLogo = this.bismillahType === 'logo';
                    
                    if (this.bismillahType === 'logo') {
                        try {
                            this.bismillahLogoUrl = require('@/data/0.png');
                        } catch {
                            this.bismillahLogoUrl = null;
                        }
                        segment.arabicText = '';
                        segment.fullArabicText = '';
                    } else {
                        this.bismillahLogoUrl = null;
                        const basUrl = `https://api.quran.com/api/v4/verses/by_key/1:1?language=en&words=true&word_fields=text_uthmani,text_indopak&fields=text_uthmani,text_indopak`;
                        try {
                            const basRes = await this.axios.get(basUrl);
                            const basVerse = basRes.data.verse;
                            let arabicText = (basVerse[fontField] || basVerse.text_uthmani || 'بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ').trim();
                            arabicText = arabicText.replace(/[\u06DD][\u0660-\u0669\u0030-\u0039]*/g, '').replace(/[\uFD3E\uFD3F]/g, '').trim();
                            segment.arabicText = arabicText;
                            segment.fullArabicText = segment.arabicText;
                        } catch {
                            segment.arabicText = 'بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ';
                            segment.fullArabicText = segment.arabicText;
                        }
                    }
                    
                    const langId = this.selectedTranslation?.id || 1;
                    try {
                        const wbwData = await this.fetchWbwTranslation(langId);
                        const basWords = wbwData?.['1']?.['1']?.[0];
                        if (basWords && Array.isArray(basWords)) {
                            segment.translationText = basWords.join(' ');
                        } else {
                            segment.translationText = 'In the name of Allah, the Most Gracious, the Most Merciful';
                        }
                    } catch {
                        segment.translationText = 'In the name of Allah, the Most Gracious, the Most Merciful';
                    }
                    segment.words = [];
                    this.$forceUpdate();
                    this.drawPreview();
                    return;
                }
                
                const surahNum = this.detectedSurah || 1;
                const ayahNum = segment.ayah || 1;
                const maxAyahs = parseInt(this.surahArray[surahNum - 1]?.ayas || 7);
                
                if (ayahNum > maxAyahs) {
                    segment.arabicText = `[Ayah ${ayahNum} - out of range for Surah ${surahNum}]`;
                    segment.translationText = '';
                    this.$forceUpdate();
                    return;
                }
                
                const verseKey = `${surahNum}:${ayahNum}`;
                const url = `https://api.quran.com/api/v4/verses/by_key/${verseKey}?language=en&words=true&word_fields=text_uthmani,text_indopak&fields=text_uthmani,text_indopak`;
                const response = await this.axios.get(url);
                const verse = response.data.verse;
                
                // Remove U+06DF (small high rounded zero) only for KFGQPC font - renders incorrectly
                const isKfgqpc = this.selectedFont?.id === 'kfgqpc';
                const cleanText = (t) => {
                    if (!t) return '';
                    return isKfgqpc ? t.replace(/\u06DF/g, '').trim() : t.trim();
                };
                
                let arabicText = cleanText(verse[fontField] || verse.text_uthmani || '');
                segment.fullArabicText = arabicText;
                
                if (segment.isPartial && verse.words && verse.words.length > 0) {
                    const wordCount = verse.words.filter(w => w.char_type_name === 'word').length;
                    const ratio = segment.partialRatio || 0.7;
                    const showCount = Math.max(1, Math.floor(wordCount * ratio));
                    const words = verse.words.filter(w => w.char_type_name === 'word').slice(0, showCount);
                    let partialText = words.map(w => cleanText(w[wordField] || w.text_uthmani || w.text || '')).join(' ').trim();
                    segment.arabicText = partialText;
                } else {
                    segment.arabicText = arabicText;
                }
                
                if (verse.words && verse.words.length > 0) {
                    segment.words = verse.words.filter(w => w.char_type_name === 'word').map((w, idx) => ({
                        text: cleanText(w[wordField] || w.text_uthmani || w.text || ''),
                        position: idx,
                        charType: w.char_type_name
                    }));
                    segment.totalWords = segment.words.length;
                    
                    // Handle AI-provided word range (already converted to 0-based in analyzeAudio)
                    if (segment.hasAiWordRange) {
                        // Clamp AI values to valid range
                        segment.startWord = Math.max(0, Math.min(segment.startWord, segment.totalWords - 1));
                        segment.endWord = Math.max(0, Math.min(segment.endWord, segment.totalWords - 1));
                        if (segment.startWord > segment.endWord) {
                            segment.startWord = 0;
                            segment.endWord = segment.totalWords - 1;
                        }
                    } else {
                        // No AI word range - use defaults
                        if (typeof segment.startWord !== 'number' || segment.startWord < 0 || segment.startWord >= segment.totalWords) {
                            segment.startWord = 0;
                        }
                        if (typeof segment.endWord !== 'number' || segment.endWord < 0 || segment.endWord >= segment.totalWords) {
                            segment.endWord = segment.totalWords - 1;
                        }
                        if (segment.startWord > segment.endWord) {
                            segment.startWord = 0;
                            segment.endWord = segment.totalWords - 1;
                        }
                    }
                }
                
                const langId = this.selectedTranslation?.id || 1;
                const wbwData = await this.fetchWbwTranslation(langId);
                segment.translationText = this.getSegmentWbwTranslation(segment, wbwData);
                
                if (segment.useManualText && segment.manualText) {
                    this.updateSegmentDisplayText(segment);
                } else if (segment.words && segment.words.length > 0) {
                    const hasTrimming = segment.startWord > 0 || segment.endWord < segment.totalWords - 1;
                    if (hasTrimming || segment.hasAiWordRange) {
                        this.updateSegmentDisplayText(segment);
                    }
                }
                
                this.$forceUpdate();
            } catch (error) {
                console.error('Error fetching ayah:', error.message || error);
                segment.arabicText = `[Error loading Ayah ${segment.ayah}]`;
                segment.translationText = '';
                this.$forceUpdate();
            }
        },
        
        onSegmentTimeChange(segment) {
            if (segment.startTime < 0) segment.startTime = 0;
            if (segment.startTime > this.audioDuration) segment.startTime = this.audioDuration - 0.5;
            if (segment.endTime < segment.startTime + 0.1) segment.endTime = segment.startTime + 0.5;
            if (segment.endTime > this.audioDuration) segment.endTime = this.audioDuration;
            this.$forceUpdate();
        },
        
        timelineZoomIn() {
            const steps = [1, 2, 3, 4, 5, 8, 10, 15, 20];
            const currentIdx = steps.indexOf(this.timelineZoom);
            if (currentIdx < steps.length - 1) {
                this.timelineZoom = steps[currentIdx + 1];
            } else if (this.timelineZoom < 20) {
                this.timelineZoom = Math.min(this.timelineZoom + 2, 20);
            }
        },
        timelineZoomOut() {
            const steps = [1, 2, 3, 4, 5, 8, 10, 15, 20];
            const currentIdx = steps.indexOf(this.timelineZoom);
            if (currentIdx > 0) {
                this.timelineZoom = steps[currentIdx - 1];
            } else if (this.timelineZoom > 1) {
                this.timelineZoom = Math.max(this.timelineZoom - 2, 1);
            }
        },
        getSegmentStyle(segment) {
            if (!this.audioDuration || this.audioDuration === 0) {
                return { left: '0%', width: '10%' };
            }
            const left = (segment.startTime / this.audioDuration) * 100;
            const width = ((segment.endTime - segment.startTime) / this.audioDuration) * 100;
            return {
                left: left + '%',
                width: Math.max(width, 2) + '%'
            };
        },
        
        startTimelineDrag(segmentIndex, handle, event) {
            const getClientX = (e) => {
                if (e.touches && e.touches.length > 0) {
                    return e.touches[0].clientX;
                }
                return e.clientX;
            };
            
            const dragState = { index: segmentIndex, handle };
            if (handle === 'move') {
                const track = this.$refs.timelineContainer.querySelector('.timeline-track');
                const rect = track.getBoundingClientRect();
                const clickTime = ((getClientX(event) - rect.left) / rect.width) * this.audioDuration;
                const segment = this.segments[segmentIndex];
                dragState.offsetFromStart = clickTime - segment.startTime;
                dragState.segmentDuration = segment.endTime - segment.startTime;
            }
            this.draggingSegment = dragState;
            
            const moveHandler = (e) => this.onTimelineDrag(e, getClientX);
            const upHandler = () => {
                document.removeEventListener('mousemove', moveHandler);
                document.removeEventListener('mouseup', upHandler);
                document.removeEventListener('touchmove', moveHandler);
                document.removeEventListener('touchend', upHandler);
                this.draggingSegment = null;
            };
            
            document.addEventListener('mousemove', moveHandler);
            document.addEventListener('mouseup', upHandler);
            document.addEventListener('touchmove', moveHandler, { passive: false });
            document.addEventListener('touchend', upHandler);
        },
        
        onTimelineDrag(event, getClientX) {
            if (!this.draggingSegment || !this.$refs.timelineContainer) return;
            if (event.cancelable) event.preventDefault();
            
            const track = this.$refs.timelineContainer.querySelector('.timeline-track');
            const rect = track.getBoundingClientRect();
            const clientX = getClientX ? getClientX(event) : event.clientX;
            const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
            const time = (x / rect.width) * this.audioDuration;
            
            const idx = this.draggingSegment.index;
            const segment = this.segments[idx];
            const prevSegment = idx > 0 ? this.segments[idx - 1] : null;
            const nextSegment = idx < this.segments.length - 1 ? this.segments[idx + 1] : null;
            const minGap = 0.1;
            
            if (this.draggingSegment.handle === 'move') {
                const duration = this.draggingSegment.segmentDuration;
                let newStart = time - this.draggingSegment.offsetFromStart;
                const minBound = prevSegment ? prevSegment.endTime + minGap : 0;
                const maxBound = (nextSegment ? nextSegment.startTime - minGap : this.audioDuration) - duration;
                newStart = Math.max(minBound, Math.min(newStart, maxBound));
                segment.startTime = newStart;
                segment.endTime = newStart + duration;
            } else if (this.draggingSegment.handle === 'start') {
                const minBound = prevSegment ? prevSegment.endTime + minGap : 0;
                const maxBound = segment.endTime - minGap;
                segment.startTime = Math.max(minBound, Math.min(time, maxBound));
            } else {
                const minBound = segment.startTime + minGap;
                const maxBound = nextSegment ? nextSegment.startTime - minGap : this.audioDuration;
                segment.endTime = Math.max(minBound, Math.min(time, maxBound));
            }
            this.$forceUpdate();
        },
        
        seekAudio(event) {
            const rect = event.currentTarget.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const percent = x / rect.width;
            if (this.$refs.previewAudio) {
                const newTime = percent * this.audioDuration;
                this.$refs.previewAudio.currentTime = newTime;
                this.syncPreviewVideo(newTime);
                this.drawPreview();
            }
        },
        
        seekFromTimeline(event) {
            if (event.target.classList.contains('timeline-track')) {
                const rect = event.currentTarget.getBoundingClientRect();
                const x = event.clientX - rect.left;
                const percent = x / rect.width;
                if (this.$refs.previewAudio) {
                    const newTime = percent * this.audioDuration;
                    this.$refs.previewAudio.currentTime = newTime;
                    this.syncPreviewVideo(newTime);
                    this.drawPreview();
                }
            }
        },
        
        changeSurah(num) {
            this.detectedSurah = parseInt(num);
            this.maxAyahs = parseInt(this.surahArray[this.detectedSurah - 1].ayas);
            this.fetchArabicSurahName(this.detectedSurah);
        },
        
        async fetchArabicSurahName(surahNum) {
            if (!surahNum || surahNum < 1) {
                this.arabicSurahName = '';
                this.englishSurahName = '';
                this.surahLogoUrl = null;
                this.colorizedSurahLogoUrl = null;
                return;
            }
            try {
                const response = await this.axios.get(`https://api.quran.com/api/v4/chapters/${surahNum}`);
                const chapter = response.data.chapter;
                this.arabicSurahName = 'سورة ' + chapter.name_arabic;
                this.englishSurahName = 'Surah ' + chapter.name_simple;
                this.surahLogoUrl = require(`@/data/title/${surahNum}.png`);
                this.updateColorizedSurahLogo();
            } catch (error) {
                console.error('Error fetching surah name:', error);
                this.arabicSurahName = '';
                this.englishSurahName = '';
                this.surahLogoUrl = null;
                this.colorizedSurahLogoUrl = null;
            }
        },
        
        updateColorizedSurahLogo() {
            if (!this.surahLogoUrl) {
                this.colorizedSurahLogoUrl = null;
                return;
            }
            
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                
                ctx.drawImage(img, 0, 0);
                ctx.globalCompositeOperation = 'source-in';
                ctx.fillStyle = this.surahLogoColor;
                ctx.fillRect(0, 0, canvas.width, canvas.height);
                
                this.colorizedSurahLogoUrl = canvas.toDataURL('image/png');
            };
            img.src = this.surahLogoUrl;
        },
        
        selectFont(font) {
            this.selectedFont = font;
            this.segments.forEach(segment => this.fetchAyahText(segment));
        },
        
        addSegment() {
            const minDuration = 2;
            let startTime = 0;
            
            if (this.segments.length > 0) {
                const lastSegment = this.segments[this.segments.length - 1];
                if (lastSegment.endTime > this.audioDuration - minDuration) {
                    lastSegment.endTime = Math.max(lastSegment.startTime + minDuration, this.audioDuration - minDuration);
                }
                startTime = lastSegment.endTime;
            }
            
            const endTime = Math.min(startTime + 5, this.audioDuration);
            
            const newSegment = {
                startTime: startTime,
                endTime: Math.max(endTime, startTime + minDuration),
                ayah: 1,
                arabicText: '',
                fullArabicText: '',
                translationText: '',
                words: [],
                startWord: 0,
                endWord: -1,
                useManualText: false,
                manualText: ''
            };
            this.segments.push(newSegment);
            this.fetchAyahText(newSegment);
        },
        
        async updateSegmentDisplayText(segment) {
            if (segment.useManualText && segment.manualText) {
                segment.arabicText = segment.manualText;
            } else if (segment.words && segment.words.length > 0) {
                const start = Math.max(0, segment.startWord || 0);
                const end = Math.min(segment.words.length - 1, segment.endWord >= 0 ? segment.endWord : segment.words.length - 1);
                const selectedWords = segment.words.slice(start, end + 1);
                segment.arabicText = selectedWords.map(w => w.text).join(' ');
            }
            const langId = this.selectedTranslation?.id || 1;
            const wbwData = await this.fetchWbwTranslation(langId);
            if (wbwData) {
                segment.translationText = this.getSegmentWbwTranslation(segment, wbwData);
            }
            this.$forceUpdate();
            this.drawPreview();
        },
        
        onWordRangeChange(segment) {
            if (segment.startWord > segment.endWord) {
                segment.startWord = segment.endWord;
            }
            this.updateSegmentDisplayText(segment);
        },
        
        toggleWordSelection(segment, wordIndex) {
            const midpoint = (segment.startWord + segment.endWord) / 2;
            
            if (wordIndex < segment.startWord) {
                segment.startWord = wordIndex;
            } else if (wordIndex > segment.endWord) {
                segment.endWord = wordIndex;
            } else if (wordIndex === segment.startWord) {
                if (segment.startWord < segment.endWord) {
                    segment.startWord = wordIndex + 1;
                }
            } else if (wordIndex === segment.endWord) {
                if (segment.endWord > segment.startWord) {
                    segment.endWord = wordIndex - 1;
                }
            } else {
                if (wordIndex <= midpoint) {
                    segment.startWord = wordIndex;
                } else {
                    segment.endWord = wordIndex;
                }
            }
            
            if (segment.startWord > segment.endWord) {
                segment.startWord = segment.endWord;
            }
            
            this.updateSegmentDisplayText(segment);
        },
        
        removeSegment(index) {
            this.segments.splice(index, 1);
        },
        
        async fetchWbwTranslation(langId) {
            if (this.wbwTranslationCache[langId]) {
                return this.wbwTranslationCache[langId];
            }
            try {
                const url = `/api/wbw-translations/${langId}`;
                const response = await this.axios.get(url);
                this.wbwTranslationCache[langId] = response.data;
                return response.data;
            } catch (error) {
                console.error('Error fetching word-by-word translations:', error);
                return null;
            }
        },
        
        getSegmentWbwTranslation(segment, wbwData) {
            if (!wbwData || !segment.words || segment.words.length === 0) return '';
            const surah = this.detectedSurah || 1;
            const ayah = segment.ayah;
            
            if (ayah === 0) {
                const basWords = wbwData['1']?.['1']?.[0];
                if (basWords && Array.isArray(basWords)) {
                    return basWords.join(' ');
                }
                return 'In the name of Allah, the Most Gracious, the Most Merciful';
            }
            
            const ayahData = wbwData[String(surah)]?.[String(ayah)];
            if (!ayahData || !ayahData[0]) return '';
            
            const allWords = ayahData[0];
            const startWord = typeof segment.startWord === 'number' ? segment.startWord : 0;
            const endWord = typeof segment.endWord === 'number' ? segment.endWord : allWords.length - 1;
            
            const selectedWords = allWords.slice(startWord, endWord + 1);
            return selectedWords.join(' ');
        },
        
        selectTranslation(trans) {
            this.selectedTranslation = trans;
            this.updateAllSegmentTranslations();
        },
        
        async updateAllSegmentTranslations() {
            const langId = this.selectedTranslation?.id || 1;
            const wbwData = await this.fetchWbwTranslation(langId);
            if (wbwData) {
                for (const segment of this.segments) {
                    segment.translationText = this.getSegmentWbwTranslation(segment, wbwData);
                }
                this.$forceUpdate();
                this.drawPreview();
            }
        },
        
        setAspectRatio(ratio) {
            this.aspectRatio = ratio;
            const baseWidth = 640;
            switch(ratio) {
                case '16:9':
                    this.videoWidth = baseWidth;
                    this.videoHeight = Math.round(baseWidth * 9 / 16);
                    break;
                case '9:16':
                    this.videoHeight = baseWidth;
                    this.videoWidth = Math.round(baseWidth * 9 / 16);
                    break;
                case '1:1':
                    this.videoWidth = baseWidth;
                    this.videoHeight = baseWidth;
                    break;
            }
            this.$nextTick(() => this.drawPreview());
        },
        
        handleBgImage(e) {
            const file = e.target.files[0];
            if (file) {
                const maxSizeMB = 10;
                const maxSizeBytes = maxSizeMB * 1024 * 1024;
                if (file.size > maxSizeBytes) {
                    this.$notify({
                        message: `Image file is too large. Maximum size is ${maxSizeMB}MB. Your file is ${(file.size / 1024 / 1024).toFixed(1)}MB.`,
                        type: 'danger'
                    });
                    e.target.value = '';
                    return;
                }
                const reader = new FileReader();
                reader.onload = (event) => {
                    this.backgroundImage = event.target.result;
                    this.selectedPreset = null;
                    this.drawPreview();
                };
                reader.readAsDataURL(file);
            }
        },
        
        selectPresetImage(img) {
            this.selectedPreset = img;
            this.backgroundImage = img;
            this.drawPreview();
        },
        
        selectPresetVideo(vid) {
            this.selectedPresetVideo = vid.url;
            this.backgroundVideo = vid.preview || vid.url;
            this.backgroundVideoFile = null;
            this.drawPreview();
        },
        
        handleBgVideo(e) {
            const file = e.target.files[0];
            if (file) {
                const maxSizeMB = 50;
                const maxSizeBytes = maxSizeMB * 1024 * 1024;
                if (file.size > maxSizeBytes) {
                    this.$notify({
                        message: `Video file is too large. Maximum size is ${maxSizeMB}MB. Your file is ${(file.size / 1024 / 1024).toFixed(1)}MB.`,
                        type: 'danger'
                    });
                    e.target.value = '';
                    return;
                }
                this.backgroundVideoFile = file;
                this.backgroundVideo = URL.createObjectURL(file);
                this.selectedPresetVideo = null;
                this.drawPreview();
            }
        },
        
        handleClientLogo(e) {
            const file = e.target.files[0];
            if (file) {
                this.clientLogoFile = file;
                const reader = new FileReader();
                reader.onload = (event) => {
                    this.clientLogo = event.target.result;
                    this.drawPreview();
                };
                reader.readAsDataURL(file);
            }
        },
        
        removeClientLogo() {
            this.clientLogo = null;
            this.clientLogoFile = null;
            this.drawPreview();
        },
        
        getClientLogoStyle() {
            return {
                left: this.clientLogoPosition.x + '%',
                top: this.clientLogoPosition.y + '%',
                transform: 'translate(-50%, -50%)',
                cursor: 'move'
            };
        },
        
        getSurahNameStyle() {
            const style = {
                left: this.surahNamePosition.x + '%',
                top: this.surahNamePosition.y + '%',
                transform: 'translate(-50%, -50%)',
                cursor: 'move'
            };
            if (this.showTextShadow) {
                style.filter = `drop-shadow(${this.textShadowOffsetX}px ${this.textShadowOffsetY}px ${this.textShadowBlur}px ${this.textShadowColor})`;
            }
            return style;
        },
        
        getEnglishSurahStyle() {
            const style = {
                left: this.englishSurahNamePosition.x + '%',
                top: this.englishSurahNamePosition.y + '%',
                transform: 'translate(-50%, -50%)',
                cursor: 'move'
            };
            if (this.showTextShadow) {
                style.textShadow = `${this.textShadowOffsetX}px ${this.textShadowOffsetY}px ${this.textShadowBlur}px ${this.textShadowColor}`;
            }
            return style;
        },
        
        getWatermarkStyle() {
            return {
                right: '12px',
                bottom: '12px',
                pointerEvents: 'none',
                opacity: 0.85
            };
        },
        
        drawPreview() {
            if (this.drawPreviewPending) return;
            this.drawPreviewPending = true;
            
            requestAnimationFrame(() => {
                this.drawPreviewPending = false;
                this._doDrawPreview();
            });
        },
        
        _doDrawPreview() {
            const canvas = this.$refs.previewCanvas;
            if (!canvas) return;
            
            const ctx = canvas.getContext('2d');
            ctx.clearRect(0, 0, this.videoWidth, this.videoHeight);
            
            const drawContent = () => {
                const currentSegment = this.getCurrentSegment();
                
                // Calculate fade opacity for smooth transitions
                let fadeOpacity = 1.0;
                const fadeDuration = 0.25; // Quick, snappy fade
                
                if (currentSegment) {
                    const timeInSegment = this.currentTime - currentSegment.startTime;
                    const segmentDuration = currentSegment.endTime - currentSegment.startTime;
                    const timeToEnd = segmentDuration - timeInSegment;
                    
                    // Fade in at start of segment with ease-out curve
                    if (timeInSegment < fadeDuration) {
                        const t = timeInSegment / fadeDuration;
                        fadeOpacity = 1 - Math.pow(1 - t, 2); // Ease-out quadratic
                    }
                    // Fade out at end of segment with ease-in curve
                    else if (timeToEnd < fadeDuration) {
                        const t = timeToEnd / fadeDuration;
                        fadeOpacity = 1 - Math.pow(1 - t, 2); // Ease-out quadratic
                    }
                    
                    fadeOpacity = Math.max(0, Math.min(1, fadeOpacity));
                }
                
                // Apply fade opacity to all text content
                ctx.globalAlpha = fadeOpacity;
                let arabicText = currentSegment?.arabicText || this.currentArabicText || '';
                const translationText = currentSegment?.translationText || this.currentTranslation || '';
                
                const fontFamily = this.selectedFont?.cssFont || "'Amiri', serif";
                const arabicX = this.videoWidth * (this.arabicPosition.x / 100);
                const arabicY = this.videoHeight * (this.arabicPosition.y / 100);
                
                // Handle bismillah logo
                if (currentSegment?.isBismillah && currentSegment?.useLogo && this.bismillahLogoUrl) {
                    const logoImg = new Image();
                    logoImg.onload = () => {
                        const logoHeight = this.arabicFontSize * 2;
                        const aspectRatio = logoImg.width / logoImg.height;
                        const logoWidth = logoHeight * aspectRatio;
                        
                        // Create temporary canvas for colorizing
                        const tempCanvas = document.createElement('canvas');
                        tempCanvas.width = logoWidth;
                        tempCanvas.height = logoHeight;
                        const tempCtx = tempCanvas.getContext('2d');
                        
                        // Draw original logo
                        tempCtx.drawImage(logoImg, 0, 0, logoWidth, logoHeight);
                        
                        // Apply color tint
                        tempCtx.globalCompositeOperation = 'source-in';
                        tempCtx.fillStyle = this.bismillahLogoColor;
                        tempCtx.fillRect(0, 0, logoWidth, logoHeight);
                        
                        if (this.showTextShadow) {
                            ctx.shadowColor = this.textShadowColor;
                            ctx.shadowOffsetX = this.textShadowOffsetX;
                            ctx.shadowOffsetY = this.textShadowOffsetY;
                            ctx.shadowBlur = this.textShadowBlur;
                        }
                        
                        ctx.globalAlpha = fadeOpacity;
                        ctx.drawImage(tempCanvas, arabicX - logoWidth / 2, arabicY - logoHeight / 2);
                        ctx.globalAlpha = 1.0;
                        
                        if (this.showTextShadow) {
                            ctx.shadowColor = 'transparent';
                            ctx.shadowOffsetX = 0;
                            ctx.shadowOffsetY = 0;
                            ctx.shadowBlur = 0;
                        }
                    };
                    logoImg.src = this.bismillahLogoUrl;
                } else if (arabicText) {
                    ctx.font = `${this.arabicFontSize}px ${fontFamily}`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'alphabetic';
                    ctx.direction = 'rtl';
                    
                    const maxWidth = this.videoWidth * (this.arabicWidth / 100);
                    const lineHeightPx = this.arabicFontSize * this.arabicLineHeight;
                    
                    if (this.shouldShowAyahMarker(currentSegment)) {
                        const markerText = this.getAyahMarker(currentSegment.ayah);
                        const isSaleemFont = this.selectedFont?.id === 'pdms';
                        const baseMarkerSize = isSaleemFont 
                            ? Math.max(10, this.arabicFontSize * 0.45)
                            : Math.max(12, this.arabicFontSize - 6);
                        const markerFontSize = baseMarkerSize * (this.ayahMarkerSize / 100);
                        this.wrapTextWithMarker(ctx, arabicText, markerText, markerFontSize, arabicX, arabicY, maxWidth, lineHeightPx, this.arabicTextColor, true);
                    } else {
                        this.wrapTextWithBackground(ctx, arabicText, arabicX, arabicY, maxWidth, lineHeightPx, this.arabicTextColor, true);
                    }
                }
                
                if (this.showTranslation && translationText) {
                    const transX = this.videoWidth * (this.translationPosition.x / 100);
                    const transY = this.videoHeight * (this.translationPosition.y / 100);
                    
                    ctx.font = `${this.translationFontSize}px 'Segoe UI', sans-serif`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.direction = 'ltr';
                    
                    const maxWidth = this.videoWidth * (this.translationWidth / 100);
                    this.wrapTextWithBackground(ctx, translationText, transX, transY, maxWidth, this.translationFontSize * 1.3, this.translationTextColor);
                }
                
                // Reset globalAlpha after drawing text content
                ctx.globalAlpha = 1.0;
                };
            
            const bgSrc = this.backgroundType === 'image' ? this.backgroundImage : 
                          this.backgroundType === 'video' ? this.backgroundVideo : null;
            
            if (this.backgroundType === 'image' && this.backgroundImage) {
                if (this.cachedBackgroundImage && this.cachedBackgroundSrc === bgSrc) {
                    ctx.drawImage(this.cachedBackgroundImage, 0, 0, this.videoWidth, this.videoHeight);
                    drawContent();
                } else {
                    const img = new Image();
                    img.crossOrigin = 'anonymous';
                    img.onload = () => {
                        this.cachedBackgroundImage = img;
                        this.cachedBackgroundSrc = bgSrc;
                        ctx.drawImage(img, 0, 0, this.videoWidth, this.videoHeight);
                        drawContent();
                    };
                    img.src = this.backgroundImage;
                }
            } else if (this.backgroundType === 'video' && this.backgroundVideo) {
                if (this.cachedBackgroundVideo && this.cachedBackgroundSrc === bgSrc) {
                    if (this.isPlaying && this.cachedBackgroundVideo.paused) {
                        this.cachedBackgroundVideo.play();
                    } else if (!this.isPlaying && !this.cachedBackgroundVideo.paused) {
                        this.cachedBackgroundVideo.pause();
                    }
                    ctx.drawImage(this.cachedBackgroundVideo, 0, 0, this.videoWidth, this.videoHeight);
                    drawContent();
                    if (this.isPlaying) {
                        this.videoAnimationId = requestAnimationFrame(() => this.drawPreview());
                    }
                } else {
                    if (this.videoAnimationId) {
                        cancelAnimationFrame(this.videoAnimationId);
                        this.videoAnimationId = null;
                    }
                    if (this.cachedBackgroundVideo) {
                        this.cachedBackgroundVideo.pause();
                    }
                    const video = document.createElement('video');
                    video.crossOrigin = 'anonymous';
                    video.muted = true;
                    video.loop = true;
                    video.playsInline = true;
                    video.preload = 'metadata';
                    video.onloadeddata = () => {
                        this.cachedBackgroundVideo = video;
                        this.cachedBackgroundSrc = bgSrc;
                        video.currentTime = 0.01;
                    };
                    video.onseeked = () => {
                        ctx.drawImage(video, 0, 0, this.videoWidth, this.videoHeight);
                        drawContent();
                    };
                    video.onerror = () => {
                        ctx.fillStyle = '#1a1a2e';
                        ctx.fillRect(0, 0, this.videoWidth, this.videoHeight);
                        ctx.fillStyle = '#ffffff';
                        ctx.font = '16px sans-serif';
                        ctx.textAlign = 'center';
                        ctx.fillText('Video Background', this.videoWidth / 2, this.videoHeight / 2);
                        drawContent();
                    };
                    video.src = this.backgroundVideo;
                }
            } else if (this.backgroundType === 'transparent') {
                this.drawCheckerboard(ctx, this.videoWidth, this.videoHeight);
                drawContent();
            } else {
                ctx.fillStyle = this.backgroundColor;
                ctx.fillRect(0, 0, this.videoWidth, this.videoHeight);
                drawContent();
            }
        },
        
        drawCheckerboard(ctx, width, height) {
            const squareSize = 20;
            const lightColor = '#ffffff';
            const darkColor = '#cccccc';
            
            for (let y = 0; y < height; y += squareSize) {
                for (let x = 0; x < width; x += squareSize) {
                    const isLight = ((x / squareSize) + (y / squareSize)) % 2 === 0;
                    ctx.fillStyle = isLight ? lightColor : darkColor;
                    ctx.fillRect(x, y, squareSize, squareSize);
                }
            }
        },
        
        wrapTextWithMarker(ctx, text, markerText, markerFontSize, x, y, maxWidth, lineHeight, textColor, isArabic = false) {
            const words = text.split(' ');
            let line = '';
            let lines = [];
            
            for (let i = 0; i < words.length; i++) {
                const testLine = line + words[i] + ' ';
                const metrics = ctx.measureText(testLine);
                if (metrics.width > maxWidth && i > 0) {
                    lines.push(line.trim());
                    line = words[i] + ' ';
                } else {
                    line = testLine;
                }
            }
            lines.push(line.trim());
            
            const baselineOffset = isArabic ? lineHeight * 0.35 : 0;
            const startY = y - ((lines.length - 1) * lineHeight) / 2 + baselineOffset;
            const padding = 8;
            
            if (this.showTextBackground && lines.length > 0) {
                let maxLineWidth = 0;
                lines.forEach(l => {
                    const w = ctx.measureText(l).width;
                    if (w > maxLineWidth) maxLineWidth = w;
                });
                
                ctx.save();
                ctx.font = `${markerFontSize}px ${this.selectedMarkerFontCss}`;
                const markerWidth = ctx.measureText(markerText).width;
                ctx.restore();
                
                const lastLineWidth = ctx.measureText(lines[lines.length - 1]).width + markerWidth;
                if (lastLineWidth > maxLineWidth) maxLineWidth = lastLineWidth;
                
                const bgHeight = lines.length * lineHeight + padding * 2;
                const bgWidth = maxLineWidth + padding * 2;
                const bgX = x - bgWidth / 2;
                const bgY = startY - lineHeight / 2 - padding;
                
                const opacity = this.textBackgroundOpacity / 100;
                const hexColor = this.textBackgroundColor;
                const r = parseInt(hexColor.slice(1, 3), 16);
                const g = parseInt(hexColor.slice(3, 5), 16);
                const b = parseInt(hexColor.slice(5, 7), 16);
                
                ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
                ctx.beginPath();
                ctx.roundRect(bgX, bgY, bgWidth, bgHeight, 6);
                ctx.fill();
            }
            
            if (this.showTextShadow) {
                ctx.shadowColor = this.textShadowColor;
                ctx.shadowOffsetX = this.textShadowOffsetX;
                ctx.shadowOffsetY = this.textShadowOffsetY;
                ctx.shadowBlur = this.textShadowBlur;
            }
            
            ctx.fillStyle = textColor;
            
            let markerWidthForOffset = 0;
            if (markerText) {
                ctx.save();
                ctx.font = `${markerFontSize}px ${this.selectedMarkerFontCss}`;
                markerWidthForOffset = ctx.measureText(markerText).width + 4;
                ctx.restore();
            }
            
            lines.forEach((l, idx) => {
                const lineY = startY + idx * lineHeight;
                if (idx === lines.length - 1 && markerText) {
                    const textWidth = ctx.measureText(l).width;
                    const offsetX = x + markerWidthForOffset / 2;
                    ctx.fillText(l, offsetX, lineY);
                    
                    ctx.save();
                    ctx.font = `${markerFontSize}px ${this.selectedMarkerFontCss}`;
                    const markerX = offsetX - textWidth / 2 - ctx.measureText(markerText).width / 2 - 2;
                    ctx.fillText(markerText, markerX, lineY);
                    ctx.restore();
                } else {
                    ctx.fillText(l, x, lineY);
                }
            });
            
            if (this.showTextShadow) {
                ctx.shadowColor = 'transparent';
                ctx.shadowOffsetX = 0;
                ctx.shadowOffsetY = 0;
                ctx.shadowBlur = 0;
            }
        },
        
        wrapTextWithBackground(ctx, text, x, y, maxWidth, lineHeight, textColor, isArabic = false) {
            const words = text.split(' ');
            let line = '';
            let lines = [];
            
            for (let i = 0; i < words.length; i++) {
                const testLine = line + words[i] + ' ';
                const metrics = ctx.measureText(testLine);
                if (metrics.width > maxWidth && i > 0) {
                    lines.push(line.trim());
                    line = words[i] + ' ';
                } else {
                    line = testLine;
                }
            }
            lines.push(line.trim());
            
            const baselineOffset = isArabic ? lineHeight * 0.35 : 0;
            const startY = y - ((lines.length - 1) * lineHeight) / 2 + baselineOffset;
            const padding = 8;
            
            if (this.showTextBackground && lines.length > 0) {
                let maxLineWidth = 0;
                lines.forEach(l => {
                    const w = ctx.measureText(l).width;
                    if (w > maxLineWidth) maxLineWidth = w;
                });
                
                const bgHeight = lines.length * lineHeight + padding * 2;
                const bgWidth = maxLineWidth + padding * 2;
                const bgX = x - bgWidth / 2;
                const bgY = startY - lineHeight / 2 - padding;
                
                const opacity = this.textBackgroundOpacity / 100;
                const hexColor = this.textBackgroundColor;
                const r = parseInt(hexColor.slice(1, 3), 16);
                const g = parseInt(hexColor.slice(3, 5), 16);
                const b = parseInt(hexColor.slice(5, 7), 16);
                
                ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
                ctx.beginPath();
                ctx.roundRect(bgX, bgY, bgWidth, bgHeight, 6);
                ctx.fill();
            }
            
            if (this.showTextShadow) {
                ctx.shadowColor = this.textShadowColor;
                ctx.shadowOffsetX = this.textShadowOffsetX;
                ctx.shadowOffsetY = this.textShadowOffsetY;
                ctx.shadowBlur = this.textShadowBlur;
            }
            
            ctx.fillStyle = textColor;
            lines.forEach((l, idx) => {
                ctx.fillText(l, x, startY + idx * lineHeight);
            });
            
            if (this.showTextShadow) {
                ctx.shadowColor = 'transparent';
                ctx.shadowOffsetX = 0;
                ctx.shadowOffsetY = 0;
                ctx.shadowBlur = 0;
            }
        },
        
        wrapText(ctx, text, x, y, maxWidth, lineHeight) {
            const words = text.split(' ');
            let line = '';
            let lines = [];
            
            for (let i = 0; i < words.length; i++) {
                const testLine = line + words[i] + ' ';
                const metrics = ctx.measureText(testLine);
                if (metrics.width > maxWidth && i > 0) {
                    lines.push(line.trim());
                    line = words[i] + ' ';
                } else {
                    line = testLine;
                }
            }
            lines.push(line.trim());
            
            const startY = y - ((lines.length - 1) * lineHeight) / 2;
            lines.forEach((l, idx) => {
                ctx.fillText(l, x, startY + idx * lineHeight);
            });
        },
        
        toggleBismillahSegment() {
            if (this.showBismillah) {
                const hasBismillah = this.segments.some(s => s.ayah === 0);
                if (!hasBismillah) {
                    const bismillahSegment = {
                        ayah: 0,
                        startTime: 0,
                        endTime: Math.min(3, this.audioDuration || 3),
                        arabicText: '',
                        fullArabicText: '',
                        translationText: '',
                        words: []
                    };
                    this.segments.unshift(bismillahSegment);
                    this.fetchAyahText(bismillahSegment);
                }
            } else {
                this.segments = this.segments.filter(s => s.ayah !== 0);
            }
            this.$forceUpdate();
        },
        
        togglePlayback() {
            const audio = this.$refs.previewAudio;
            if (!audio) return;
            
            if (this.isPlaying) {
                audio.pause();
                this.isPlaying = false;
            } else {
                const playPromise = audio.play();
                if (playPromise !== undefined) {
                    playPromise.then(() => {
                        this.isPlaying = true;
                        this.drawPreview();
                    }).catch(() => {
                        this.isPlaying = false;
                    });
                } else {
                    this.isPlaying = true;
                }
            }
        },
        
        seekToTime(time) {
            const audio = this.$refs.previewAudio;
            if (audio) {
                audio.currentTime = parseFloat(time);
                this.currentTime = parseFloat(time);
                this.playbackProgress = (this.currentTime / this.audioDuration) * 100;
                this.syncPreviewVideo(this.currentTime);
                this.drawPreview();
            }
        },
        
        syncPreviewVideo(time) {
            if (this.cachedBackgroundVideo && this.backgroundType === 'video') {
                const videoDuration = this.cachedBackgroundVideo.duration || 1;
                this.cachedBackgroundVideo.currentTime = time % videoDuration;
            }
        },
        
        onTimeUpdate() {
            const audio = this.$refs.previewAudio;
            if (!audio) return;
            
            if (this.isSeeking) return;
            
            this.currentTime = audio.currentTime;
            this.playbackProgress = (this.currentTime / this.audioDuration) * 100;
            
            const segIdx = this.segments.findIndex(s => 
                this.currentTime >= s.startTime && this.currentTime < s.endTime
            );
            this.currentSegmentIndex = segIdx;
            
            this.drawPreview();
        },
        
        onAudioEnded() {
            this.isPlaying = false;
            if (this.cachedBackgroundVideo && !this.cachedBackgroundVideo.paused) {
                this.cachedBackgroundVideo.pause();
            }
            if (this.videoAnimationId) {
                cancelAnimationFrame(this.videoAnimationId);
                this.videoAnimationId = null;
            }
            this.drawPreview();
        },
        
        onAudioPaused() {
            this.isPlaying = false;
            if (this.cachedBackgroundVideo && !this.cachedBackgroundVideo.paused) {
                this.cachedBackgroundVideo.pause();
            }
            if (this.videoAnimationId) {
                cancelAnimationFrame(this.videoAnimationId);
                this.videoAnimationId = null;
            }
        },
        
        getCurrentSegment() {
            const activeSegment = this.segments.find(s => {
                if (s.ayah === 0 && !this.showBismillah) return false;
                return this.currentTime >= s.startTime && this.currentTime <= s.endTime;
            });
            
            if (activeSegment) return activeSegment;
            
            const validSegments = this.segments.filter(s => !(s.ayah === 0 && !this.showBismillah));
            if (validSegments.length > 0 && this.currentTime <= validSegments[0].startTime) {
                return validSegments[0];
            }
            
            return null;
        },
        
        startDrag(target, event) {
            this.dragTarget = target;
            
            const container = this.$refs.previewContainer;
            if (!container) return;
            
            const overlayRect = event.target.getBoundingClientRect();
            
            const overlayCenterX = overlayRect.left + overlayRect.width / 2;
            const overlayCenterY = overlayRect.top + overlayRect.height / 2;
            
            this.dragOffset = {
                x: event.clientX - overlayCenterX,
                y: event.clientY - overlayCenterY
            };
            
            document.addEventListener('mousemove', this.onDrag);
            document.addEventListener('mouseup', this.stopDrag);
        },
        
        onDrag(event) {
            if (!this.dragTarget) return;
            
            const container = this.$refs.previewContainer;
            if (!container) return;
            
            const rect = container.getBoundingClientRect();
            
            const centerX = event.clientX - this.dragOffset.x - rect.left;
            const centerY = event.clientY - this.dragOffset.y - rect.top;
            
            const x = (centerX / rect.width) * 100;
            const y = (centerY / rect.height) * 100;
            
            if (this.dragTarget === 'arabic') {
                this.arabicPosition = { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
            } else if (this.dragTarget === 'translation') {
                this.translationPosition = { x: Math.max(0, Math.min(100, x)), y: Math.max(0, Math.min(100, y)) };
            } else if (this.dragTarget === 'clientLogo') {
                this.clientLogoPosition = { x: Math.max(5, Math.min(95, x)), y: Math.max(5, Math.min(95, y)) };
            } else if (this.dragTarget === 'surahName') {
                this.surahNamePosition = { x: Math.max(5, Math.min(95, x)), y: Math.max(5, Math.min(95, y)) };
            } else if (this.dragTarget === 'englishSurah') {
                this.englishSurahNamePosition = { x: Math.max(5, Math.min(95, x)), y: Math.max(5, Math.min(95, y)) };
            }
        },
        
        stopDrag() {
            this.dragTarget = null;
            document.removeEventListener('mousemove', this.onDrag);
            document.removeEventListener('mouseup', this.stopDrag);
        },
        
        getArabicOverlayStyle() {
            const font = this.selectedFont || this.fontOptions[0];
            return {
                left: this.arabicPosition.x + '%',
                top: this.arabicPosition.y + '%',
                width: this.arabicWidth + '%',
                height: this.arabicHeight + '%',
                transform: 'translate(-50%, -50%)',
                fontFamily: font.cssFont,
                fontSize: this.arabicFontSize + 'px',
                color: this.arabicTextColor
            };
        },
        
        getTranslationOverlayStyle() {
            return {
                left: this.translationPosition.x + '%',
                top: this.translationPosition.y + '%',
                width: this.translationWidth + '%',
                height: this.translationHeight + '%',
                transform: 'translate(-50%, -50%)',
                fontSize: this.translationFontSize + 'px',
                color: this.translationTextColor
            };
        },
        
        startResize(target, direction, event) {
            this.resizeTarget = target;
            this.resizeDirection = direction;
            document.addEventListener('mousemove', this.onResize);
            document.addEventListener('mouseup', this.stopResize);
            event.preventDefault();
        },
        
        onResize(event) {
            if (!this.resizeTarget || !this.resizeDirection) return;
            
            const container = this.$refs.previewContainer;
            if (!container) return;
            
            const rect = container.getBoundingClientRect();
            const x = ((event.clientX - rect.left) / rect.width) * 100;
            const y = ((event.clientY - rect.top) / rect.height) * 100;
            
            if (this.resizeTarget === 'arabic') {
                if (this.resizeDirection.includes('e')) {
                    const currentX = this.arabicPosition.x;
                    const newWidth = Math.abs(x - currentX) * 2;
                    this.arabicWidth = Math.max(20, Math.min(100, newWidth));
                }
                if (this.resizeDirection.includes('s')) {
                    const currentY = this.arabicPosition.y;
                    const newHeight = Math.abs(y - currentY) * 2;
                    this.arabicHeight = Math.max(10, Math.min(80, newHeight));
                }
            } else if (this.resizeTarget === 'translation') {
                if (this.resizeDirection.includes('e')) {
                    const currentX = this.translationPosition.x;
                    const newWidth = Math.abs(x - currentX) * 2;
                    this.translationWidth = Math.max(20, Math.min(100, newWidth));
                }
                if (this.resizeDirection.includes('s')) {
                    const currentY = this.translationPosition.y;
                    const newHeight = Math.abs(y - currentY) * 2;
                    this.translationHeight = Math.max(10, Math.min(80, newHeight));
                }
            }
        },
        
        stopResize() {
            this.resizeTarget = null;
            this.resizeDirection = null;
            document.removeEventListener('mousemove', this.onResize);
            document.removeEventListener('mouseup', this.stopResize);
            this.drawPreview();
        },
        
        computeSegmentLayouts(exportScale) {
            const canvas = this.$refs.previewCanvas;
            if (!canvas) return [];
            
            const ctx = canvas.getContext('2d');
            const layouts = [];
            
            for (const segment of this.segments) {
                const arabicText = segment.arabicText || '';
                const translationText = segment.translationText || '';
                
                const fontFamily = this.selectedFont?.cssFont || "'Amiri', serif";
                ctx.font = `${this.arabicFontSize}px ${fontFamily}`;
                ctx.textAlign = 'center';
                ctx.direction = 'rtl';
                
                const maxArabicWidth = this.videoWidth * (this.arabicWidth / 100);
                const arabicLines = this.computeLines(ctx, arabicText, maxArabicWidth);
                
                const arabicLineHeight = this.arabicFontSize * this.arabicLineHeight;
                const arabicXPx = this.videoWidth * (this.arabicPosition.x / 100);
                const arabicYPx = this.videoHeight * (this.arabicPosition.y / 100);
                const arabicStartY = arabicYPx - ((arabicLines.length - 1) * arabicLineHeight) / 2;
                
                let transLines = [];
                let transStartY = 0;
                let transXPx = 0;
                let transLineHeight = this.translationFontSize * 1.3;
                
                if (this.showTranslation && translationText) {
                    ctx.font = `${this.translationFontSize}px 'Segoe UI', sans-serif`;
                    ctx.textAlign = 'center';
                    ctx.direction = 'ltr';
                    
                    const maxTransWidth = this.videoWidth * (this.translationWidth / 100);
                    transLines = this.computeLines(ctx, translationText, maxTransWidth);
                    
                    transXPx = this.videoWidth * (this.translationPosition.x / 100);
                    const transYPx = this.videoHeight * (this.translationPosition.y / 100);
                    transStartY = transYPx - ((transLines.length - 1) * transLineHeight) / 2;
                }
                
                layouts.push({
                    ayah: segment.ayah,
                    startTime: segment.startTime,
                    endTime: segment.endTime,
                    arabicLines: arabicLines,
                    arabicX: Math.round(arabicXPx * exportScale),
                    arabicStartY: Math.round(arabicStartY * exportScale),
                    arabicLineHeight: Math.round(arabicLineHeight * exportScale),
                    translationLines: transLines,
                    translationX: Math.round(transXPx * exportScale),
                    translationStartY: Math.round(transStartY * exportScale),
                    translationLineHeight: Math.round(transLineHeight * exportScale)
                });
            }
            
            return layouts;
        },
        
        computeLines(ctx, text, maxWidth) {
            if (!text) return [];
            const words = text.split(' ');
            let line = '';
            let lines = [];
            
            for (let i = 0; i < words.length; i++) {
                const testLine = line + words[i] + ' ';
                const metrics = ctx.measureText(testLine);
                if (metrics.width > maxWidth && i > 0) {
                    lines.push(line.trim());
                    line = words[i] + ' ';
                } else {
                    line = testLine;
                }
            }
            if (line.trim()) {
                lines.push(line.trim());
            }
            return lines;
        },
        
        async generateSegmentPNG(segment, exportWidth, exportHeight, exportScale) {
            const canvas = document.createElement('canvas');
            canvas.width = exportWidth;
            canvas.height = exportHeight;
            const ctx = canvas.getContext('2d');
            
            ctx.clearRect(0, 0, exportWidth, exportHeight);
            
            let arabicText = segment.arabicText || '';
            const translationText = segment.translationText || '';
            
            const showMarker = this.shouldShowAyahMarker(segment);
            const markerText = showMarker ? this.getAyahMarker(segment.ayah) : '';
            
            const fontFamily = this.selectedFont?.cssFont || "'Amiri', serif";
            const arabicFontSize = Math.round(this.arabicFontSize * exportScale);
            const isSaleemFont = this.selectedFont?.id === 'pdms';
            const baseMarkerSize = isSaleemFont 
                ? Math.max(10 * exportScale / 3, arabicFontSize * 0.45)
                : Math.max(12 * exportScale / 3, arabicFontSize - (6 * exportScale / 3));
            const markerFontSize = baseMarkerSize * (this.ayahMarkerSize / 100);
            const transFontSize = Math.round(this.translationFontSize * exportScale);
            
            const arabicX = exportWidth * (this.arabicPosition.x / 100);
            const arabicY = exportHeight * (this.arabicPosition.y / 100);
            const maxArabicWidth = exportWidth * (this.arabicWidth / 100);
            const arabicLineHeight = arabicFontSize * this.arabicLineHeight;
            
            // Handle bismillah logo
            if (segment.isBismillah && segment.useLogo && this.bismillahLogoUrl) {
                const logoHeight = arabicFontSize * 2;
                const img = new Image();
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                    img.src = this.bismillahLogoUrl;
                });
                const aspectRatio = img.width / img.height;
                const logoWidth = logoHeight * aspectRatio;
                
                // Create temporary canvas for colorizing
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = logoWidth;
                tempCanvas.height = logoHeight;
                const tempCtx = tempCanvas.getContext('2d');
                
                // Draw original logo
                tempCtx.drawImage(img, 0, 0, logoWidth, logoHeight);
                
                // Apply color tint
                tempCtx.globalCompositeOperation = 'source-in';
                tempCtx.fillStyle = this.bismillahLogoColor;
                tempCtx.fillRect(0, 0, logoWidth, logoHeight);
                
                if (this.showTextShadow) {
                    ctx.shadowColor = this.textShadowColor;
                    ctx.shadowOffsetX = this.textShadowOffsetX * exportScale;
                    ctx.shadowOffsetY = this.textShadowOffsetY * exportScale;
                    ctx.shadowBlur = this.textShadowBlur * exportScale;
                }
                
                ctx.drawImage(tempCanvas, arabicX - logoWidth / 2, arabicY - logoHeight / 2);
                
                if (this.showTextShadow) {
                    ctx.shadowColor = 'transparent';
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                    ctx.shadowBlur = 0;
                }
            } else if (arabicText) {
                ctx.font = `${arabicFontSize}px ${fontFamily}`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.direction = 'rtl';
                
                const arabicLines = this.computeLines(ctx, arabicText, maxArabicWidth);
                const arabicStartY = arabicY - ((arabicLines.length - 1) * arabicLineHeight) / 2;
                const padding = arabicFontSize * 0.3;
                
                if (this.showTextBackground && arabicLines.length > 0) {
                    let maxLineWidth = 0;
                    arabicLines.forEach(l => {
                        const w = ctx.measureText(l).width;
                        if (w > maxLineWidth) maxLineWidth = w;
                    });
                    
                    if (showMarker) {
                        ctx.save();
                        ctx.font = `${markerFontSize}px ${this.selectedMarkerFontCss}`;
                        const markerWidth = ctx.measureText(markerText).width;
                        ctx.restore();
                        ctx.font = `${arabicFontSize}px ${fontFamily}`;
                        const lastLineWidth = ctx.measureText(arabicLines[arabicLines.length - 1]).width + markerWidth;
                        if (lastLineWidth > maxLineWidth) maxLineWidth = lastLineWidth;
                    }
                    
                    const bgHeight = arabicLines.length * arabicLineHeight + padding * 2;
                    const bgWidth = maxLineWidth + padding * 2;
                    const bgX = arabicX - bgWidth / 2;
                    const bgY = arabicStartY - arabicLineHeight / 2 - padding;
                    
                    const opacity = this.textBackgroundOpacity / 100;
                    const hexColor = this.textBackgroundColor;
                    const r = parseInt(hexColor.slice(1, 3), 16);
                    const g = parseInt(hexColor.slice(3, 5), 16);
                    const b = parseInt(hexColor.slice(5, 7), 16);
                    
                    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
                    ctx.beginPath();
                    ctx.roundRect(bgX, bgY, bgWidth, bgHeight, 12 * exportScale / 3);
                    ctx.fill();
                }
                
                if (this.showTextShadow) {
                    ctx.shadowColor = this.textShadowColor;
                    ctx.shadowOffsetX = this.textShadowOffsetX * exportScale;
                    ctx.shadowOffsetY = this.textShadowOffsetY * exportScale;
                    ctx.shadowBlur = this.textShadowBlur * exportScale;
                }
                
                ctx.fillStyle = this.arabicTextColor;
                
                let markerWidthForOffset = 0;
                if (showMarker) {
                    ctx.save();
                    ctx.font = `${markerFontSize}px ${this.selectedMarkerFontCss}`;
                    markerWidthForOffset = ctx.measureText(markerText).width + 4;
                    ctx.restore();
                    ctx.font = `${arabicFontSize}px ${fontFamily}`;
                }
                
                arabicLines.forEach((line, idx) => {
                    const lineY = arabicStartY + idx * arabicLineHeight;
                    if (showMarker && idx === arabicLines.length - 1) {
                        const textWidth = ctx.measureText(line).width;
                        const offsetX = arabicX + markerWidthForOffset / 2;
                        ctx.fillText(line, offsetX, lineY);
                        
                        ctx.save();
                        ctx.font = `${markerFontSize}px ${this.selectedMarkerFontCss}`;
                        const markerX = offsetX - textWidth / 2 - ctx.measureText(markerText).width / 2 - 2;
                        ctx.fillText(markerText, markerX, lineY);
                        ctx.restore();
                    } else {
                        ctx.fillText(line, arabicX, lineY);
                    }
                });
                
                if (this.showTextShadow) {
                    ctx.shadowColor = 'transparent';
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                    ctx.shadowBlur = 0;
                }
            }
            
            if (this.showTranslation && translationText) {
                const transX = exportWidth * (this.translationPosition.x / 100);
                const transY = exportHeight * (this.translationPosition.y / 100);
                const maxTransWidth = exportWidth * (this.translationWidth / 100);
                const transLineHeight = transFontSize * 1.3;
                
                ctx.font = `${transFontSize}px 'Segoe UI', sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.direction = 'ltr';
                
                const transLines = this.computeLines(ctx, translationText, maxTransWidth);
                const transStartY = transY - ((transLines.length - 1) * transLineHeight) / 2;
                const transPadding = transFontSize * 0.3;
                
                if (this.showTextBackground && transLines.length > 0) {
                    let maxLineWidth = 0;
                    transLines.forEach(l => {
                        const w = ctx.measureText(l).width;
                        if (w > maxLineWidth) maxLineWidth = w;
                    });
                    
                    const bgHeight = transLines.length * transLineHeight + transPadding * 2;
                    const bgWidth = maxLineWidth + transPadding * 2;
                    const bgX = transX - bgWidth / 2;
                    const bgY = transStartY - transLineHeight / 2 - transPadding;
                    
                    const opacity = this.textBackgroundOpacity / 100;
                    const hexColor = this.textBackgroundColor;
                    const r = parseInt(hexColor.slice(1, 3), 16);
                    const g = parseInt(hexColor.slice(3, 5), 16);
                    const b = parseInt(hexColor.slice(5, 7), 16);
                    
                    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${opacity})`;
                    ctx.beginPath();
                    ctx.roundRect(bgX, bgY, bgWidth, bgHeight, 8 * exportScale / 3);
                    ctx.fill();
                }
                
                if (this.showTextShadow) {
                    ctx.shadowColor = this.textShadowColor;
                    ctx.shadowOffsetX = this.textShadowOffsetX * exportScale;
                    ctx.shadowOffsetY = this.textShadowOffsetY * exportScale;
                    ctx.shadowBlur = this.textShadowBlur * exportScale;
                }
                
                ctx.fillStyle = this.translationTextColor;
                transLines.forEach((line, idx) => {
                    ctx.fillText(line, transX, transStartY + idx * transLineHeight);
                });
                
                if (this.showTextShadow) {
                    ctx.shadowColor = 'transparent';
                    ctx.shadowOffsetX = 0;
                    ctx.shadowOffsetY = 0;
                    ctx.shadowBlur = 0;
                }
            }
            
            return canvas.toDataURL('image/png');
        },
        
        generateWatermarkPNG(exportWidth, exportHeight, exportScale) {
            const canvas = document.createElement('canvas');
            canvas.width = exportWidth;
            canvas.height = exportHeight;
            const ctx = canvas.getContext('2d');
            
            ctx.clearRect(0, 0, exportWidth, exportHeight);
            
            const fontSize = Math.round(14 * exportScale);
            ctx.font = `${fontSize}px 'Segoe UI', sans-serif`;
            ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
            ctx.textAlign = 'right';
            ctx.textBaseline = 'bottom';
            ctx.fillText('QuranClipHelper.com', exportWidth - 10, exportHeight - 10);
            
            return canvas.toDataURL('image/png');
        },
        
        async generateSurahNamePNG(exportWidth, exportHeight, exportScale) {
            const canvas = document.createElement('canvas');
            canvas.width = exportWidth;
            canvas.height = exportHeight;
            const ctx = canvas.getContext('2d');
            
            ctx.clearRect(0, 0, exportWidth, exportHeight);
            
            const surahX = exportWidth * (this.surahNamePosition.x / 100);
            let surahY = exportHeight * (this.surahNamePosition.y / 100);
            
            // Apply shadow if enabled
            if (this.showTextShadow) {
                ctx.shadowColor = this.textShadowColor;
                ctx.shadowOffsetX = this.textShadowOffsetX * exportScale;
                ctx.shadowOffsetY = this.textShadowOffsetY * exportScale;
                ctx.shadowBlur = this.textShadowBlur * exportScale;
            }
            
            if (this.surahNameType === 'logo' && this.surahLogoUrl) {
                const logoHeight = Math.round(this.surahNameSize * exportScale);
                const img = new Image();
                await new Promise((resolve, reject) => {
                    img.onload = resolve;
                    img.onerror = reject;
                    img.src = this.surahLogoUrl;
                });
                const aspectRatio = img.width / img.height;
                const logoWidth = logoHeight * aspectRatio;
                
                // Create temporary canvas for colorizing
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = logoWidth;
                tempCanvas.height = logoHeight;
                const tempCtx = tempCanvas.getContext('2d');
                
                // Draw original logo
                tempCtx.drawImage(img, 0, 0, logoWidth, logoHeight);
                
                // Apply color tint
                tempCtx.globalCompositeOperation = 'source-in';
                tempCtx.fillStyle = this.surahLogoColor;
                tempCtx.fillRect(0, 0, logoWidth, logoHeight);
                
                ctx.drawImage(tempCanvas, surahX - logoWidth / 2, surahY - logoHeight / 2);
                surahY += logoHeight / 2 + 10;
            } else if (this.currentSurahName) {
                const surahFontSize = Math.round(this.surahNameSize * exportScale);
                ctx.font = `${surahFontSize}px 'Amiri Quran', 'Amiri', serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.direction = 'rtl';
                ctx.fillStyle = this.surahNameColor;
                ctx.fillText(this.currentSurahName, surahX, surahY);
                surahY += surahFontSize / 2 + 10;
            }
            
            if (this.showEnglishSurahName && this.englishSurahName) {
                const engFontSize = Math.round(this.englishSurahNameSize * exportScale);
                const engX = exportWidth * (this.englishSurahNamePosition.x / 100);
                const engY = exportHeight * (this.englishSurahNamePosition.y / 100);
                ctx.font = `${engFontSize}px 'Segoe UI', sans-serif`;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.direction = 'ltr';
                ctx.fillStyle = this.englishSurahNameColor;
                ctx.fillText(this.englishSurahName, engX, engY);
            }
            
            // Reset shadow
            if (this.showTextShadow) {
                ctx.shadowColor = 'transparent';
                ctx.shadowOffsetX = 0;
                ctx.shadowOffsetY = 0;
                ctx.shadowBlur = 0;
            }
            
            return canvas.toDataURL('image/png');
        },
        
        async exportVideo() {
            this.isExporting = true;
            this.exportProgress = 0;
            this.exportElapsedSeconds = 0;
            this.exportStartTime = Date.now();
            this.exportTimer = setInterval(() => {
                this.exportElapsedSeconds = Math.floor((Date.now() - this.exportStartTime) / 1000);
            }, 1000);
            
            if (typeof window.gtag === 'function') {
                window.gtag('event', 'video_export_start', {
                    event_category: 'video_creator',
                    surah: this.detectedSurah,
                    ayah_count: this.segments.length,
                    aspect_ratio: this.aspectRatio,
                    background_type: this.backgroundType,
                    has_translation: this.showTranslation,
                    font: this.selectedFont?.name || 'unknown'
                });
            }
            
            try {
                const exportScale = this.aspectRatio === '16:9' ? 1920 / this.videoWidth :
                                   this.aspectRatio === '9:16' ? 1080 / this.videoWidth :
                                   1080 / this.videoWidth;
                const exportWidth = this.aspectRatio === '16:9' ? 1920 :
                                   this.aspectRatio === '9:16' ? 1080 : 1080;
                const exportHeight = this.aspectRatio === '16:9' ? 1080 :
                                    this.aspectRatio === '9:16' ? 1920 : 1080;
                
                const segmentOverlays = await Promise.all(this.segments.map(async (segment, index) => {
                    const pngDataUrl = await this.generateSegmentPNG(segment, exportWidth, exportHeight, exportScale);
                    return {
                        index: index,
                        startTime: segment.startTime,
                        endTime: segment.endTime,
                        pngData: pngDataUrl
                    };
                }));
                
                const watermarkPng = this.generateWatermarkPNG(exportWidth, exportHeight, exportScale);
                
                let surahNamePng = null;
                if (this.showSurahName && (this.currentSurahName || this.surahLogoUrl)) {
                    surahNamePng = await this.generateSurahNamePNG(exportWidth, exportHeight, exportScale);
                }
                
                const config = {
                    surah: this.detectedSurah,
                    segments: this.segments.map(s => ({
                        ayah: s.ayah,
                        startTime: s.startTime,
                        endTime: s.endTime
                    })),
                    segmentOverlays: segmentOverlays,
                    watermarkPng: watermarkPng,
                    surahNamePng: surahNamePng,
                    aspectRatio: this.aspectRatio,
                    videoWidth: exportWidth,
                    videoHeight: exportHeight,
                    backgroundColor: this.backgroundColor,
                    backgroundType: this.backgroundType,
                    backgroundVideoUrl: this.backgroundType === 'video' && this.selectedPresetVideo ? this.selectedPresetVideo : null,
                    hasClientLogo: !!this.clientLogo,
                    clientLogoSize: Math.round(this.clientLogoSize * exportScale),
                    clientLogoPosition: this.clientLogoPosition,
                    fadeInDuration: 0.4,
                    fadeOutDuration: 0.4
                };
                
                const formData = new FormData();
                
                if (!this.audioFile) {
                    throw new Error('Audio file is missing. Please upload audio again.');
                }
                
                formData.append('audio', this.audioFile);
                formData.append('config', JSON.stringify(config));
                
                if (this.backgroundType === 'image' && this.backgroundImage) {
                    const bgBlob = await fetch(this.backgroundImage).then(r => r.blob());
                    formData.append('background', bgBlob);
                } else if (this.backgroundType === 'video' && this.backgroundVideoFile) {
                    formData.append('backgroundVideo', this.backgroundVideoFile);
                }
                
                if (this.clientLogoFile) {
                    formData.append('clientLogo', this.clientLogoFile);
                }
                
                const submitResponse = await this.apiRequest('post', `${this.apiBaseUrl}/api/jobs/submit`, formData);
                
                if (!submitResponse.data.success) {
                    throw new Error(submitResponse.data.error || 'Failed to submit job');
                }
                
                this.jobId = submitResponse.data.jobId;
                this.jobStatus = 'pending';
                this.exportProgress = 5;
                
                await this.pollJobStatus();
                
            } catch (error) {
                console.error('Export error:', error);
                const errorMessage = error.response?.data?.error || error.message || 'Failed to export video. Please try again.';
                this.$notify({
                    message: errorMessage,
                    type: 'danger'
                });
                this.cleanupExport();
            }
        },
        
        async pollJobStatus() {
            const pollInterval = 1500;
            const maxPolls = 600;
            let pollCount = 0;
            
            const poll = async () => {
                if (!this.jobId || !this.isExporting) {
                    this.cleanupExport();
                    return;
                }
                
                try {
                    const response = await this.apiRequest('get', `${this.apiBaseUrl}/api/jobs/${this.jobId}/status`);
                    
                    const data = response.data;
                    
                    if (!data.success) {
                        throw new Error(data.error || 'Failed to get job status');
                    }
                    
                    this.jobStatus = data.status;
                    this.exportProgress = data.progress || 0;
                    this.jobStage = data.stage || '';
                    
                    if (data.status === 'completed') {
                        await this.downloadJobResult();
                        return;
                    } else if (data.status === 'failed') {
                        throw new Error(data.error || 'Video rendering failed');
                    }
                    
                    pollCount++;
                    if (pollCount >= maxPolls) {
                        throw new Error('Video rendering timed out');
                    }
                    
                    this.jobPollingInterval = setTimeout(poll, pollInterval);
                    
                } catch (error) {
                    console.error('Poll error:', error);
                    this.$notify({
                        message: error.message || 'Failed to check job status',
                        type: 'danger'
                    });
                    this.cleanupExport();
                }
            };
            
            await poll();
        },
        
        async downloadJobResult() {
            try {
                const response = await this.apiRequest('get', `${this.apiBaseUrl}/api/jobs/${this.jobId}/download`, null, {
                    responseType: 'blob'
                });
                
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                const fileExtension = this.backgroundType === 'transparent' ? 'webm' : 'mp4';
                link.setAttribute('download', `quran_video_${this.detectedSurah}.${fileExtension}`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                window.URL.revokeObjectURL(url);
                
                if (typeof window.gtag === 'function') {
                    const exportDuration = Math.floor((Date.now() - this.exportStartTime) / 1000);
                    window.gtag('event', 'video_export_complete', {
                        event_category: 'video_creator',
                        surah: this.detectedSurah,
                        ayah_count: this.segments.length,
                        aspect_ratio: this.aspectRatio,
                        background_type: this.backgroundType,
                        export_duration_seconds: exportDuration
                    });
                }
                
            } catch (error) {
                console.error('Download error:', error);
                this.$notify({
                    message: 'Failed to download video. Please try again.',
                    type: 'danger'
                });
            } finally {
                this.cleanupExport();
            }
        },
        
        cleanupExport() {
            if (this.exportTimer) {
                clearInterval(this.exportTimer);
                this.exportTimer = null;
            }
            if (this.jobPollingInterval) {
                clearTimeout(this.jobPollingInterval);
                this.jobPollingInterval = null;
            }
            this.isExporting = false;
            this.exportProgress = 0;
            this.jobId = null;
            this.jobStatus = null;
            this.jobStage = '';
        }
    },
    watch: {
        currentStep(newVal) {
            if (newVal === 3) {
                this.$nextTick(() => {
                    this.currentSegmentIndex = 0;
                    this.drawPreview();
                    this.fetchQueueStatus();
                });
            }
        },
        backgroundColor() {
            this.cachedBackgroundSrc = null;
            this.drawPreview();
        },
        backgroundType() {
            if (this.videoAnimationId) {
                cancelAnimationFrame(this.videoAnimationId);
                this.videoAnimationId = null;
            }
            if (this.cachedBackgroundVideo) {
                this.cachedBackgroundVideo.pause();
            }
            this.cachedBackgroundSrc = null;
            this.cachedBackgroundImage = null;
            this.cachedBackgroundVideo = null;
            this.drawPreview();
        },
        backgroundImage() {
            this.cachedBackgroundSrc = null;
            this.cachedBackgroundImage = null;
            this.drawPreview();
        },
        backgroundVideo() {
            this.cachedBackgroundSrc = null;
            this.cachedBackgroundVideo = null;
            this.drawPreview();
        },
        async selectedFont(newFont) {
            await this.ensureFontLoaded(newFont);
            await Promise.all(this.segments.map(segment => this.fetchAyahText(segment)));
            this.$nextTick(() => this.drawPreview());
        },
        arabicFontSize(newSize, oldSize) {
            if (oldSize && oldSize > 0) {
                const scale = newSize / oldSize;
                this.arabicHeight = Math.max(10, Math.min(80, this.arabicHeight * scale));
            }
            this.prevArabicFontSize = newSize;
            this.drawPreview();
        },
        arabicLineHeight() {
            this.drawPreview();
        },
        arabicTextColor() {
            this.drawPreview();
        },
        showTextBackground() {
            this.drawPreview();
        },
        textBackgroundColor() {
            this.drawPreview();
        },
        showTextShadow() {
            this.drawPreview();
        },
        textShadowColor() {
            this.drawPreview();
        },
        textShadowOffsetX() {
            this.drawPreview();
        },
        textShadowOffsetY() {
            this.drawPreview();
        },
        textShadowBlur() {
            this.drawPreview();
        },
        textBackgroundOpacity() {
            this.drawPreview();
        },
        arabicPosition: {
            deep: true,
            handler() {
                this.drawPreview();
            }
        },
        translationFontSize() {
            this.drawPreview();
        },
        translationTextColor() {
            this.drawPreview();
        },
        translationPosition: {
            deep: true,
            handler() {
                this.drawPreview();
            }
        },
        showTranslation() {
            this.drawPreview();
        },
        showBismillah(newVal) {
            if (newVal) {
                const hasBismillah = this.segments.some(s => s.ayah === 0);
                if (!hasBismillah && this.segments.length > 0) {
                    const firstSegment = this.segments[0];
                    const bismillahDuration = 3;
                    const bismillahEndTime = Math.min(bismillahDuration, this.audioDuration * 0.3);
                    
                    const bismillahSegment = {
                        ayah: 0,
                        startTime: 0,
                        endTime: bismillahEndTime,
                        arabicText: '',
                        fullArabicText: '',
                        translationText: '',
                        words: []
                    };
                    this.segments.unshift(bismillahSegment);
                    this.fetchAyahText(bismillahSegment);
                    
                    if (firstSegment.startTime < bismillahEndTime + 1) {
                        firstSegment.startTime = bismillahEndTime + 1;
                        if (firstSegment.endTime <= firstSegment.startTime) {
                            firstSegment.endTime = firstSegment.startTime + 2;
                        }
                    }
                }
            } else {
                this.segments = this.segments.filter(s => s.ayah !== 0);
            }
            this.$forceUpdate();
            this.drawPreview();
        },
        bismillahType() {
            const bismillahSegment = this.segments.find(s => s.ayah === 0);
            if (bismillahSegment) {
                this.fetchAyahText(bismillahSegment);
            }
        },
        bismillahLogoColor() {
            this.drawPreview();
        },
        showAyahNumber() {
            this.drawPreview();
        },
        ayahMarkerFont() {
            this.drawPreview();
        },
        ayahMarkerSize() {
            this.drawPreview();
        },
        arabicWidth() {
            this.drawPreview();
        },
        translationWidth() {
            this.drawPreview();
        },
        clientLogoSize() {
            this.drawPreview();
        },
        clientLogoPosition: {
            deep: true,
            handler() {
                this.drawPreview();
            }
        },
        showSurahName() {
            this.drawPreview();
        },
        surahNameSize() {
            this.drawPreview();
        },
        surahNameColor() {
            this.drawPreview();
        },
        surahLogoColor() {
            this.updateColorizedSurahLogo();
            this.drawPreview();
        },
        surahLogoUrl() {
            this.updateColorizedSurahLogo();
        },
        surahNamePosition: {
            deep: true,
            handler() {
                this.drawPreview();
            }
        },
        englishSurahNamePosition: {
            deep: true,
            handler() {
                this.drawPreview();
            }
        }
    },
    beforeDestroy() {
        if (this.audioUrl) {
            URL.revokeObjectURL(this.audioUrl);
        }
        if (this.exportTimer) {
            clearInterval(this.exportTimer);
        }
    }
}
</script>

<style scoped>
.upload-zone {
    border: 3px dashed #5e72e4;
    border-radius: 10px;
    background: #f8f9fe;
    transition: all 0.3s ease;
    cursor: pointer;
}

.upload-zone:hover,
.upload-zone.drag-over {
    border-color: #2dce89;
    background: #e8f5e9;
}

.timeline-container {
    max-height: 300px;
    overflow-y: auto;
}

.segment-item {
    background: #f8f9fe;
}

.arabic-preview {
    font-size: 18px;
    color: #172b4d;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 1.8;
}

.word-trim-controls {
    background: #fafafa;
}

.word-pill {
    background: #e0e0e0;
    color: #666;
    transition: all 0.2s ease;
}

.word-pill:hover {
    background: #d0d0d0;
}

.word-pill.selected {
    background: #5e72e4;
    color: white;
}

.preview-container {
    position: relative;
    background: #000;
    border-radius: 8px;
    overflow: hidden;
}

.preview-container canvas {
    display: block;
    width: 100%;
    height: auto;
}

.overlay-controls {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    pointer-events: none;
}

.position-control {
    position: absolute;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: move;
    pointer-events: auto;
    transform: translate(-50%, -50%);
    font-size: 12px;
    opacity: 0.8;
    transition: opacity 0.2s;
}

.position-control:hover {
    opacity: 1;
}

.arabic-control {
    background: rgba(46, 204, 113, 0.9);
    color: white;
    border: 2px solid white;
}

.translation-control {
    background: rgba(52, 152, 219, 0.9);
    color: white;
    border: 2px solid white;
}

.text-overlay {
    position: absolute;
    pointer-events: auto;
    cursor: move;
    padding: 10px 15px;
    border-radius: 4px;
    background: transparent;
    border: 2px dashed rgba(255, 255, 255, 0.6);
    white-space: pre-wrap;
    word-wrap: break-word;
    text-align: center;
    user-select: none;
    min-height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.text-overlay:hover {
    border-color: rgba(255, 255, 255, 0.8);
}

.arabic-overlay {
    font-family: 'KFGQPC Uthmanic Script HAFS', 'Amiri Quran', 'Traditional Arabic', serif;
}

.translation-overlay {
    font-family: 'Inter', sans-serif;
}

.resize-handle {
    position: absolute;
    width: 10px;
    height: 10px;
    background: #3498db;
    border: 2px solid white;
    border-radius: 2px;
}

.resize-handle.resize-e {
    right: -6px;
    top: 50%;
    transform: translateY(-50%);
    cursor: e-resize;
}

.resize-handle.resize-s {
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    cursor: s-resize;
}

.resize-handle.resize-se {
    right: -6px;
    bottom: -6px;
    cursor: se-resize;
}

.arabic-overlay .resize-handle {
    background: #2ecc71;
}

.settings-panel {
    max-height: calc(100vh - 200px);
    overflow-y: auto;
}

.section-header {
    cursor: pointer;
    padding: 8px 0;
    transition: background-color 0.2s;
}

.section-header:hover {
    background-color: rgba(0, 0, 0, 0.03);
    border-radius: 4px;
}

.section-header h5 {
    user-select: none;
}

.section-content {
    padding-left: 4px;
}

.preset-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
}

.preset-thumb {
    position: relative;
    aspect-ratio: 16/9;
    border-radius: 4px;
    overflow: hidden;
    cursor: pointer;
    border: 2px solid transparent;
    transition: all 0.2s ease;
}

.preset-thumb:hover {
    border-color: #5e72e4;
}

.preset-thumb.active {
    border-color: #2dce89;
    box-shadow: 0 0 0 2px rgba(45, 206, 137, 0.3);
}

.preset-thumb img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.preset-thumb.video-thumb i {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    font-size: 20px;
    color: rgba(255, 255, 255, 0.9);
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
}

.dropdown-menu {
    max-height: 200px;
    overflow-y: auto;
}

.segment-timeline {
    background: #f8f9fe;
    padding: 12px;
    border-radius: 8px;
}

.timeline-zoom-controls {
    display: flex;
    align-items: center;
    gap: 2px;
}

.timeline-zoom-controls .btn {
    font-size: 10px;
    line-height: 1;
    min-width: 22px;
}

.timeline-scrollable {
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 10px;
    scrollbar-width: auto;
    scrollbar-color: #5e72e4 #d2d6dc;
}

.timeline-scrollable::-webkit-scrollbar {
    height: 12px;
}

.timeline-scrollable::-webkit-scrollbar-track {
    background: #d2d6dc;
    border-radius: 6px;
}

.timeline-scrollable::-webkit-scrollbar-thumb {
    background: #5e72e4;
    border-radius: 6px;
}

.timeline-scrollable::-webkit-scrollbar-thumb:hover {
    background: #4a5dcf;
}

.timeline-track {
    position: relative;
    height: 48px;
    background: #e0e0e0;
    border-radius: 4px;
    cursor: pointer;
}

.timeline-playhead {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 2px;
    background: #f5365c;
    z-index: 10;
    pointer-events: none;
}

.timeline-segment {
    position: absolute;
    top: 4px;
    bottom: 4px;
    background: #5e72e4;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 20px;
    opacity: 0.8;
    transition: opacity 0.2s;
    cursor: grab;
}

.timeline-segment:active {
    cursor: grabbing;
}

.timeline-segment:hover,
.timeline-segment.active {
    opacity: 1;
}

.timeline-segment.active {
    background: #2dce89;
}

.segment-handle {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 8px;
    background: rgba(255,255,255,0.5);
    cursor: ew-resize;
    opacity: 0;
    transition: opacity 0.2s;
}

.timeline-segment:hover .segment-handle {
    opacity: 1;
}

.segment-handle.left {
    left: 0;
    border-radius: 4px 0 0 4px;
}

.segment-handle.right {
    right: 0;
    border-radius: 0 4px 4px 0;
}

@media (hover: none) and (pointer: coarse) {
    .segment-handle {
        width: 16px;
        opacity: 1;
        background: rgba(255,255,255,0.7);
    }
    .timeline-segment {
        min-height: 28px;
    }
}

.segment-label {
    color: white;
    font-size: 11px;
    font-weight: bold;
    text-shadow: 0 1px 2px rgba(0,0,0,0.3);
    pointer-events: none;
}

.video-seek-slider {
    -webkit-appearance: none;
    appearance: none;
    height: 8px;
    background: #e0e0e0;
    border-radius: 4px;
    outline: none;
    cursor: pointer;
}

.video-seek-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 18px;
    height: 18px;
    background: #5e72e4;
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.video-seek-slider::-moz-range-thumb {
    width: 18px;
    height: 18px;
    background: #5e72e4;
    border-radius: 50%;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(0,0,0,0.2);
}

.client-logo-overlay {
    position: absolute;
    pointer-events: auto;
    cursor: move;
    padding: 4px;
    border: 2px dashed rgba(255, 255, 255, 0.5);
    border-radius: 4px;
    background: rgba(0, 0, 0, 0.2);
}

.client-logo-overlay:hover {
    border-color: rgba(255, 255, 255, 0.8);
}

.client-logo-overlay img {
    display: block;
    max-width: 100%;
    pointer-events: none;
}

.surah-name-overlay {
    position: absolute;
    pointer-events: auto;
    cursor: move;
    padding: 2px;
    border: 2px dashed rgba(255, 255, 255, 0.5);
    border-radius: 2px;
    background: transparent;
    font-family: 'Amiri Quran', 'Amiri', serif;
    direction: rtl;
    white-space: nowrap;
    display: inline-block;
}

.surah-name-overlay:hover {
    border-color: rgba(255, 255, 255, 0.8);
}

.english-surah-overlay {
    position: absolute;
    pointer-events: auto;
    cursor: move;
    padding: 2px;
    border: 2px dashed rgba(255, 255, 255, 0.5);
    border-radius: 2px;
    background: transparent;
    white-space: nowrap;
    display: inline-block;
}

.english-surah-overlay:hover {
    border-color: rgba(255, 255, 255, 0.8);
}

.watermark-overlay {
    position: absolute;
    pointer-events: none;
    padding: 4px;
}

.watermark-text {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.5);
    font-family: 'Segoe UI', sans-serif;
    white-space: nowrap;
}

.client-logo-preview {
    display: flex;
    align-items: center;
    padding: 8px;
    background: #f8f9fe;
    border-radius: 4px;
}
</style>
